import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Home, Building2, Users, LogOut, Plus, Edit, Trash2, Save, X, 
  FileText, UserPlus, Mail, Phone, MapPin, Calendar, RefreshCw, 
  Loader2, CheckCircle2, Clock, AlertCircle, ClipboardList, User,
  Eye, CheckCheck, XCircle, Image, Bed, Bath, PoundSterling,
  Send, Upload, File, Video, Filter, MessageSquare, DollarSign,
  Bell, Settings, Search, MoreVertical, Camera, FolderOpen,
  Banknote, TrendingUp, Building, Sparkles, UserSearch, CalendarCheck, Copy, Download, Smartphone,
  BookOpen, Globe, Tag, ExternalLink, PenTool
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // PWA Install Prompt
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  
  // Main state
  const [activeDepartment, setActiveDepartment] = useState('estate');
  const [activeTab, setActiveTab] = useState('overview');
  const [adminName, setAdminName] = useState('');
  const [adminId, setAdminId] = useState('');
  
  // Data state
  const [listings, setListings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState({ architecture: [], landlord: [] });
  const [pendingUploads, setPendingUploads] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [propertySeekers, setPropertySeekers] = useState([]);
  const [viewingRequests, setViewingRequests] = useState([]);
  const [consultationRequests, setConsultationRequests] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [agencyAgreements, setAgencyAgreements] = useState([]);
  
  // Blog & SEO state
  const [blogPosts, setBlogPosts] = useState([]);
  const [seoSettings, setSeoSettings] = useState({
    site_title: '',
    site_description: '',
    google_analytics_id: '',
    keywords: []
  });
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'landlord',
    image: '',
    tags: '',
    featured: false,
    published: false,
    meta_title: '',
    meta_description: ''
  });
  const [editingBlog, setEditingBlog] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Forms
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'lettings_agent', department: 'estate' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '', task_type: 'general' });
  const [tenantForm, setTenantForm] = useState({ 
    name: '', email: '', phone: '', property_address: '', monthly_rent: '', 
    deposit_amount: '', deposit_scheme: '', deposit_reference: '',
    commission_type: '12percent', // '12percent' or 'custom'
    landlord_payment: '', // Fixed amount to landlord (for custom)
    landlord_name: '', landlord_email: '', landlord_phone: '', landlord_address: '', landlord_bank_details: '',
    tenancy_start: '', tenancy_end: '', rent_due_day: 1, notes: ''
  });
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Auto-calculate commission based on type
  const calculateCommission = (rent, type, customLandlordPayment) => {
    const rentAmount = parseFloat(rent) || 0;
    if (type === '12percent') {
      const landlordPayment = rentAmount * 0.88;
      const agencyProfit = rentAmount * 0.12;
      return { landlordPayment, agencyProfit };
    } else {
      const landlordPayment = parseFloat(customLandlordPayment) || 0;
      const agencyProfit = rentAmount - landlordPayment;
      return { landlordPayment, agencyProfit };
    }
  };
  const [listingForm, setListingForm] = useState({
    listing_type: 'rent', property_type: 'flat', title: '', address: '', city: '', postcode: '',
    bedrooms: 1, bathrooms: 1, price: '', description: '', features: [], images: [], videos: []
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [listingsRes, employeesRes, tasksRes, archLeadsRes, landlordLeadsRes, uploadsRes, tenantsRes, seekersRes, convRes, viewingRes, consultRes, agreementsRes, blogRes, seoRes] = await Promise.all([
        fetch(`${API_URL}/api/listings`),
        fetch(`${API_URL}/api/employees`),
        fetch(`${API_URL}/api/tasks`),
        fetch(`${API_URL}/api/leads/architecture`),
        fetch(`${API_URL}/api/leads/landlord`),
        fetch(`${API_URL}/api/portal-uploads`),
        fetch(`${API_URL}/api/tenants`),
        fetch(`${API_URL}/api/property-seekers`),
        fetch(`${API_URL}/api/conversations`),
        fetch(`${API_URL}/api/viewing-requests`),
        fetch(`${API_URL}/api/consultation-requests`),
        fetch(`${API_URL}/api/agency-agreements`),
        fetch(`${API_URL}/api/blog/posts`),
        fetch(`${API_URL}/api/seo/settings`)
      ]);
      
      if (listingsRes.ok) setListings(await listingsRes.json());
      if (employeesRes.ok) setEmployees(await employeesRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (tenantsRes.ok) setTenants(await tenantsRes.json());
      if (seekersRes.ok) setPropertySeekers(await seekersRes.json());
      if (convRes.ok) setConversations(await convRes.json());
      if (viewingRes.ok) setViewingRequests(await viewingRes.json());
      if (consultRes.ok) setConsultationRequests(await consultRes.json());
      if (agreementsRes.ok) setAgencyAgreements(await agreementsRes.json());
      if (blogRes.ok) setBlogPosts(await blogRes.json());
      if (seoRes.ok) setSeoSettings(await seoRes.json());
      setLeads({
        architecture: archLeadsRes.ok ? await archLeadsRes.json() : [],
        landlord: landlordLeadsRes.ok ? await landlordLeadsRes.json() : []
      });
      if (uploadsRes.ok) setPendingUploads(await uploadsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminLoggedIn');
    const name = localStorage.getItem('adminName');
    const id = localStorage.getItem('adminId') || 'admin';
    if (!isAdmin) {
      navigate('/admin/login');
    } else {
      setAdminName(name || 'Admin');
      setAdminId(id);
      fetchAllData();
    }
    
    // PWA Install Prompt Handler
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner if not dismissed before
      const dismissed = localStorage.getItem('pwaInstallDismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [navigate]);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast({ title: "App Installed!", description: "H&J Admin is now on your home screen" });
    }
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwaInstallDismissed', 'true');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin/login');
  };

  // Filtered data
  const pendingListings = listings.filter(l => l.status === 'pending');
  const activeListings = listings.filter(l => l.status === 'active');
  const filteredEmployees = employees.filter(e => !e.department || e.department === activeDepartment);
  const filteredTasks = tasks.filter(t => !t.department || t.department === activeDepartment);
  const filteredLeads = activeDepartment === 'architecture' ? leads.architecture : leads.landlord;
  const filteredUploads = pendingUploads.filter(u => !u.department || u.department === activeDepartment);

  // Search filter
  const searchFilter = (items, fields) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      fields.some(field => item[field]?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  // ============== HANDLERS ==============
  
  // Download document helper function
  const downloadDocument = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast({ title: "Download Started", description: `${filename} is downloading...` });
    } catch (error) {
      toast({ title: "Download Failed", description: "Unable to download document", variant: "destructive" });
    }
  };

  // Employee invite
  const handleInviteEmployee = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/employees/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inviteForm, department: activeDepartment })
      });
      const data = await response.json();
      if (response.ok) {
        if (data.email_sent) {
          toast({ title: "Invitation Sent!", description: `Email sent to ${inviteForm.email}` });
        } else {
          toast({ title: "Employee Added", description: `Employee created but email failed to send. Share setup link manually.`, variant: "destructive" });
        }
        setShowModal(null);
        setInviteForm({ email: '', name: '', role: 'agent', department: activeDepartment });
        fetchAllData();
      } else {
        toast({ title: "Error", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send invitation", variant: "destructive" });
    }
  };

  // Task CRUD
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...taskForm, assigned_by: adminName, department: activeDepartment })
      });
      if (response.ok) {
        toast({ title: "Task Created", description: "Email notification sent to employee" });
        setShowModal(null);
        setTaskForm({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '', task_type: 'general' });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create task", variant: "destructive" });
    }
  };

  // Agency Agreements
  const handleMarkAgreementSigned = async (agreementId) => {
    if (!window.confirm('Mark this agreement as signed and activate the listing?')) return;
    try {
      const response = await fetch(`${API_URL}/api/agency-agreements/${agreementId}/sign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        toast({ title: "Agreement Signed!", description: "Listing is now live. Notification sent to landlord." });
        fetchAllData();
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update agreement", variant: "destructive" });
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}`, { method: 'DELETE' });
      toast({ title: "Task Deleted" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleTaskStatus = async (taskId, status) => {
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  // Listing CRUD
  const handleApproveListing = async (id) => {
    try {
      await fetch(`${API_URL}/api/listings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' })
      });
      toast({ title: "Approved!", description: "Listing is now live" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to approve", variant: "destructive" });
    }
  };

  const handleRejectListing = async (id) => {
    try {
      await fetch(`${API_URL}/api/listings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected' })
      });
      toast({ title: "Rejected" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await fetch(`${API_URL}/api/listings/${id}`, { method: 'DELETE' });
      toast({ title: "Listing Deleted" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Edit Listing - open modal with listing data
  const handleEditListing = (listing) => {
    setListingForm({
      id: listing.id,
      listing_type: listing.listing_type || 'rent',
      property_type: listing.property_type || 'flat',
      title: listing.title || '',
      address: listing.address || '',
      city: listing.city || '',
      postcode: listing.postcode || '',
      bedrooms: listing.bedrooms || 1,
      bathrooms: listing.bathrooms || 1,
      price: listing.price || '',
      description: listing.description || '',
      features: listing.features || [],
      images: listing.images || [],
      videos: listing.videos || [],
      status: listing.status || 'active',
      available_from: listing.available_from || '',
      floor_area: listing.floor_area || '',
      reception_rooms: listing.reception_rooms || 0
    });
    setEditingItem(listing);
    setShowModal('editListing');
  };

  // Save edited listing
  const handleSaveEditedListing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/listings/${listingForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_type: listingForm.listing_type,
          property_type: listingForm.property_type,
          title: listingForm.title,
          address: listingForm.address,
          city: listingForm.city,
          postcode: listingForm.postcode,
          bedrooms: parseInt(listingForm.bedrooms) || 0,
          bathrooms: parseInt(listingForm.bathrooms) || 0,
          price: parseFloat(listingForm.price) || 0,
          description: listingForm.description,
          features: listingForm.features,
          images: listingForm.images,
          videos: listingForm.videos,
          status: listingForm.status,
          available_from: listingForm.available_from,
          floor_area: parseInt(listingForm.floor_area) || 0,
          reception_rooms: parseInt(listingForm.reception_rooms) || 0
        })
      });
      if (response.ok) {
        toast({ title: "Listing Updated!", description: "Changes saved successfully" });
        setShowModal(null);
        setEditingItem(null);
        fetchAllData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update listing", variant: "destructive" });
    }
  };

  // Property Seeker Assignment
  const handleAssignSeeker = async (seekerId, employeeId) => {
    try {
      const response = await fetch(`${API_URL}/api/property-seekers/${seekerId}/assign?employee_id=${employeeId}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast({ title: "Assigned!", description: "Task created and employee notified" });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleUpdateSeekerStatus = async (seekerId, status) => {
    try {
      await fetch(`${API_URL}/api/property-seekers/${seekerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      toast({ title: "Status Updated" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Chat Functions
  const openConversation = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/conversations/${conversationId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveConversation(data.conversation);
        setMessages(data.messages);
        await fetch(`${API_URL}/api/conversations/${conversationId}/read?reader_id=${adminId}`, { method: 'PUT' });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/conversations/${activeConversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: adminId,
          sender_name: adminName,
          sender_type: 'admin',
          message: newMessage
        })
      });
      if (res.ok) {
        setNewMessage('');
        openConversation(activeConversation.id);
      }
    } catch (error) {
      toast({ title: "Failed to send", variant: "destructive" });
    }
    setSendingMessage(false);
  };

  const startNewConversation = async (participantId, participantName, participantType) => {
    try {
      const res = await fetch(`${API_URL}/api/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          department: activeDepartment,
          participant_ids: [adminId, participantId],
          participant_names: [adminName, participantName],
          participant_types: ['admin', participantType],
          subject: `Chat with ${participantName}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        fetchAllData();
        openConversation(data.conversation_id);
        setActiveTab('messages');
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // File upload
  const handleFileUpload = async (e, type = 'image') => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    try {
      const response = await fetch(`${API_URL}/api/upload/multiple`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const newFiles = data.uploaded.map(f => ({
          url: `${API_URL}${f.url}`,
          filename: f.filename,
          original_name: f.original_name
        }));
        
        if (type === 'image') {
          setListingForm(prev => ({ ...prev, images: [...prev.images, ...newFiles] }));
        } else {
          setListingForm(prev => ({ ...prev, videos: [...prev.videos, ...newFiles] }));
        }
        toast({ title: "Upload Complete", description: `${newFiles.length} file(s) uploaded` });
      }
    } catch (error) {
      toast({ title: "Upload Failed", variant: "destructive" });
    }
    setUploading(false);
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/listings?created_by_admin=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...listingForm,
          images: listingForm.images.map(i => i.url),
          videos: listingForm.videos.map(v => v.url),
          status: 'live'
        })
      });
      if (response.ok) {
        toast({ title: "Listing Created & Live" });
        setShowModal(null);
        setListingForm({
          listing_type: 'rent', property_type: 'flat', title: '', address: '', city: '', postcode: '',
          bedrooms: 1, bathrooms: 1, price: '', description: '', features: [], images: [], videos: []
        });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Tenant/Rent
  const handleCreateTenant = async (e) => {
    e.preventDefault();
    const { landlordPayment, agencyProfit } = calculateCommission(
      tenantForm.monthly_rent, 
      tenantForm.commission_type, 
      tenantForm.landlord_payment
    );
    try {
      const response = await fetch(`${API_URL}/api/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...tenantForm,
          monthly_rent: parseFloat(tenantForm.monthly_rent) || 0,
          deposit_amount: parseFloat(tenantForm.deposit_amount) || 0,
          rent_collected: parseFloat(tenantForm.monthly_rent) || 0,
          rent_forwarded: landlordPayment,
          agency_commission: agencyProfit,
          rent_due_day: parseInt(tenantForm.rent_due_day) || 1
        })
      });
      if (response.ok) {
        toast({ title: "Tenant Added" });
        setShowModal(null);
        resetTenantForm();
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    const { landlordPayment, agencyProfit } = calculateCommission(
      tenantForm.monthly_rent, 
      tenantForm.commission_type, 
      tenantForm.landlord_payment
    );
    try {
      const response = await fetch(`${API_URL}/api/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...tenantForm,
          monthly_rent: parseFloat(tenantForm.monthly_rent) || 0,
          deposit_amount: parseFloat(tenantForm.deposit_amount) || 0,
          rent_collected: parseFloat(tenantForm.monthly_rent) || 0,
          rent_forwarded: landlordPayment,
          agency_commission: agencyProfit,
          rent_due_day: parseInt(tenantForm.rent_due_day) || 1
        })
      });
      if (response.ok) {
        toast({ title: "Tenant Updated" });
        setShowModal(null);
        setSelectedTenant(null);
        resetTenantForm();
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    try {
      const response = await fetch(`${API_URL}/api/tenants/${tenantId}`, { method: 'DELETE' });
      if (response.ok) {
        toast({ title: "Tenant Deleted" });
        setShowModal(null);
        setSelectedTenant(null);
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const resetTenantForm = () => {
    setTenantForm({ 
      name: '', email: '', phone: '', property_address: '', monthly_rent: '', 
      deposit_amount: '', deposit_scheme: '', deposit_reference: '',
      commission_type: '12percent',
      landlord_payment: '',
      landlord_name: '', landlord_email: '', landlord_phone: '', landlord_address: '', landlord_bank_details: '',
      tenancy_start: '', tenancy_end: '', rent_due_day: 1, notes: ''
    });
  };

  const openTenantDetail = (tenant) => {
    setSelectedTenant(tenant);
    // Determine commission type from existing data
    const rent = parseFloat(tenant.monthly_rent) || 0;
    const forwarded = parseFloat(tenant.rent_forwarded) || 0;
    const is12Percent = rent > 0 && Math.abs((rent * 0.88) - forwarded) < 1; // Allow £1 tolerance
    
    setTenantForm({
      name: tenant.name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      property_address: tenant.property_address || '',
      monthly_rent: tenant.monthly_rent || '',
      deposit_amount: tenant.deposit_amount || '',
      deposit_scheme: tenant.deposit_scheme || '',
      deposit_reference: tenant.deposit_reference || '',
      commission_type: is12Percent ? '12percent' : 'custom',
      landlord_payment: tenant.rent_forwarded || '',
      landlord_name: tenant.landlord_name || '',
      landlord_email: tenant.landlord_email || '',
      landlord_phone: tenant.landlord_phone || '',
      landlord_address: tenant.landlord_address || '',
      landlord_bank_details: tenant.landlord_bank_details || '',
      tenancy_start: tenant.tenancy_start || '',
      tenancy_end: tenant.tenancy_end || '',
      rent_due_day: tenant.rent_due_day || 1,
      notes: tenant.notes || ''
    });
    setShowModal('tenant-detail');
  };

  const handleSendRentReminder = async (tenantId, sendEmail = true, sendWhatsApp = true) => {
    try {
      const response = await fetch(`${API_URL}/api/tenants/${tenantId}/send-reminder`, { method: 'POST' });
      const data = await response.json();
      
      let messages = [];
      
      // Check if email was actually sent
      if (sendEmail) {
        if (data.email_sent) {
          messages.push("✓ Email sent");
        } else {
          messages.push("✗ Email failed");
        }
      }
      
      // Open WhatsApp in new tab (optional - phone might not have WhatsApp)
      if (sendWhatsApp && data.whatsapp_link) {
        window.open(data.whatsapp_link, '_blank');
        messages.push("WhatsApp opened");
      }
      
      toast({ 
        title: data.email_sent ? "Reminder Sent" : "Reminder Partially Sent",
        description: `${messages.join(" • ")} (Total: ${data.reminders_sent} reminders)`,
        variant: data.email_sent ? "default" : "destructive"
      });
      
      // Refresh data to show updated reminder count
      fetchAllData();
    } catch (error) {
      toast({ title: "Error sending reminder", variant: "destructive" });
    }
  };

  // Tenant filter and sort
  const [tenantFilter, setTenantFilter] = useState('all'); // all, paid, pending, overdue
  const [tenantSort, setTenantSort] = useState('name'); // name, rent, due_date, status
  const [tenantSearch, setTenantSearch] = useState('');

  const getFilteredTenants = () => {
    let filtered = tenants.filter(t => t.is_active !== false);
    
    // Apply search
    if (tenantSearch) {
      const search = tenantSearch.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(search) || 
        t.property_address?.toLowerCase().includes(search) ||
        t.email?.toLowerCase().includes(search) ||
        t.landlord_name?.toLowerCase().includes(search)
      );
    }
    
    // Apply filter
    if (tenantFilter !== 'all') {
      filtered = filtered.filter(t => t.payment_status === tenantFilter);
    }
    
    // Apply sort
    filtered.sort((a, b) => {
      switch (tenantSort) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'rent':
          return (b.monthly_rent || 0) - (a.monthly_rent || 0);
        case 'due_date':
          return (a.rent_due_day || 1) - (b.rent_due_day || 1);
        case 'status':
          const statusOrder = { overdue: 0, pending: 1, paid: 2 };
          return (statusOrder[a.payment_status] || 1) - (statusOrder[b.payment_status] || 1);
        case 'address':
          return (a.property_address || '').localeCompare(b.property_address || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const handleCreateRentChaseTask = (tenant) => {
    setTaskForm({
      title: `Chase Rent: ${tenant.name}`,
      description: `Follow up on rent payment for ${tenant.property_address}. Monthly rent: £${tenant.monthly_rent}. Due date: ${tenant.next_due_date || 'N/A'}. Tenant phone: ${tenant.phone}`,
      assigned_to: '',
      priority: tenant.payment_status === 'overdue' ? 'high' : 'medium',
      due_date: '',
      task_type: 'rent_chase',
      department: 'estate'
    });
    setShowModal('task');
  };

  const handleRecordPayment = async (tenantId, amount) => {
    const today = new Date().toISOString().split('T')[0];
    const month = today.substring(0, 7);
    try {
      await fetch(`${API_URL}/api/tenants/${tenantId}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount),
          payment_date: today,
          period_month: month,
          payment_method: 'bank_transfer'
        })
      });
      toast({ title: "Payment Recorded" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleUpdatePaymentStatus = async (tenantId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/tenants/${tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_status: status })
      });
      if (response.ok) {
        const statusLabels = { paid: 'PAID', pending: 'NOT PAID', vacant: 'VACANT' };
        toast({ title: `Status Updated: ${statusLabels[status]}` });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error updating status", variant: "destructive" });
    }
  };

  // ============== E-SIGNATURE HANDLERS ==============

  const [sendingSignature, setSendingSignature] = useState(null);

  const handleSendForSignature = async (tenant) => {
    if (!tenant.email) {
      toast({ title: "Error", description: "Tenant email is required to send for signature", variant: "destructive" });
      return;
    }
    
    setSendingSignature(tenant.id);
    try {
      const response = await fetch(`${API_URL}/api/send-signature-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenant.id })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({ 
          title: "Signature Request Sent!", 
          description: data.email_sent 
            ? `Email sent to ${tenant.email}. Expires in 7 days.`
            : "Request created but email failed to send. Share link manually."
        });
        fetchAllData();
      } else {
        toast({ title: "Error", description: data.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error sending signature request", variant: "destructive" });
    } finally {
      setSendingSignature(null);
    }
  };

  const getSignatureStatusBadge = (status) => {
    switch (status) {
      case 'signed':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  const getSignatureStatusLabel = (status) => {
    switch (status) {
      case 'signed':
        return '✓ Signed';
      case 'pending':
        return '⏳ Awaiting';
      case 'expired':
        return '✗ Expired';
      default:
        return 'Not Sent';
    }
  };

  // ============== BLOG HANDLERS ==============
  
  const handleSaveBlog = async () => {
    try {
      const blogData = {
        ...blogForm,
        tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()) : [],
        slug: blogForm.slug || blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').trim()
      };
      
      const url = editingBlog 
        ? `${API_URL}/api/blog/posts/${editingBlog.id}`
        : `${API_URL}/api/blog/posts`;
      
      const response = await fetch(url, {
        method: editingBlog ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blogData)
      });
      
      if (response.ok) {
        toast({ title: editingBlog ? "Blog Updated" : "Blog Created" });
        setBlogForm({
          title: '', slug: '', excerpt: '', content: '', category: 'landlord',
          image: '', tags: '', featured: false, published: false, meta_title: '', meta_description: ''
        });
        setEditingBlog(null);
        setShowModal(null);
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error saving blog", variant: "destructive" });
    }
  };

  const handleEditBlog = (blog) => {
    setBlogForm({
      ...blog,
      tags: blog.tags ? blog.tags.join(', ') : ''
    });
    setEditingBlog(blog);
    setShowModal('blog');
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await fetch(`${API_URL}/api/blog/posts/${id}`, { method: 'DELETE' });
      toast({ title: "Blog Deleted" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error deleting blog", variant: "destructive" });
    }
  };

  const handlePublishBlog = async (id, publish) => {
    try {
      const endpoint = publish ? 'publish' : 'unpublish';
      await fetch(`${API_URL}/api/blog/posts/${id}/${endpoint}`, { method: 'POST' });
      toast({ title: publish ? "Blog Published!" : "Blog Unpublished" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error updating blog", variant: "destructive" });
    }
  };

  const handleSaveSeoSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/seo/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(seoSettings)
      });
      if (response.ok) {
        toast({ title: "SEO Settings Saved" });
      }
    } catch (error) {
      toast({ title: "Error saving SEO settings", variant: "destructive" });
    }
  };

  // Upload approval
  const handleApproveUpload = async (id) => {
    try {
      await fetch(`${API_URL}/api/portal-uploads/${id}/approve`, { method: 'PUT' });
      toast({ title: "Approved" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleRejectUpload = async (id) => {
    try {
      await fetch(`${API_URL}/api/portal-uploads/${id}/reject`, { method: 'PUT' });
      toast({ title: "Rejected" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Delete employee
  const handleDeactivateEmployee = async (id) => {
    if (!window.confirm('Deactivate this employee?')) return;
    try {
      await fetch(`${API_URL}/api/employees/${id}`, { method: 'DELETE' });
      toast({ title: "Employee Deactivated" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Viewing Request Handlers
  const handleApproveViewing = async (requestId, agentId) => {
    try {
      const response = await fetch(`${API_URL}/api/viewing-requests/${requestId}/approve?agent_id=${agentId}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast({ title: "Viewing Approved", description: "Task created and assigned to agent" });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleRejectViewing = async (requestId) => {
    try {
      await fetch(`${API_URL}/api/viewing-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected' })
      });
      toast({ title: "Viewing Rejected" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Consultation Request Handlers
  const handleApproveConsultation = async (requestId, architectId) => {
    try {
      const response = await fetch(`${API_URL}/api/consultation-requests/${requestId}/approve?architect_id=${architectId}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast({ title: "Consultation Approved", description: "Task created and assigned to architect" });
        fetchAllData();
      }
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleRejectConsultation = async (requestId) => {
    try {
      await fetch(`${API_URL}/api/consultation-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'rejected' })
      });
      toast({ title: "Consultation Rejected" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // ============== UI HELPERS ==============
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-red-100 text-red-700',
      active: 'bg-green-100 text-green-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      vacant: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      in_progress: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      paid: 'PAID',
      pending: 'NOT PAID',
      vacant: 'VACANT'
    };
    return labels[status] || 'NOT PAID';
  };

  const pendingAgreements = agencyAgreements.filter(a => a.status === 'pending_signature');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'listings', label: 'Listings', icon: Building, badge: pendingListings.length },
    { id: 'agreements', label: 'Agreements', icon: FileText, badge: pendingAgreements.length },
    { id: 'viewings', label: 'Viewings', icon: CalendarCheck, badge: viewingRequests.filter(v => v.status === 'pending').length },
    { id: 'consultations', label: 'Consultations', icon: Building2, badge: consultationRequests.filter(c => c.status === 'pending').length },
    { id: 'seekers', label: 'Property Seekers', icon: UserSearch, badge: propertySeekers.filter(s => s.status === 'new').length },
    { id: 'employees', label: 'Team', icon: Users },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: conversations.reduce((a, c) => a + (c.unread_count || 0), 0) },
    { id: 'tenants', label: 'Rent Collection', icon: Banknote },
    { id: 'leads', label: 'Leads', icon: Mail, badge: filteredLeads.length },
    { id: 'blog', label: 'Blog', icon: BookOpen },
    { id: 'seo', label: 'SEO', icon: Search },
    { id: 'uploads', label: 'Uploads', icon: Upload, badge: filteredUploads.filter(u => u.status === 'pending').length },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white px-4 py-3 flex items-center justify-between gap-4 animate-slideDown">
          <div className="flex items-center gap-3">
            <Smartphone className="w-6 h-6" />
            <div>
              <p className="font-semibold text-sm">Install H&J Admin App</p>
              <p className="text-xs opacity-90">Quick access from your home screen</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleInstallApp}
              className="bg-white text-[#FF8C42] px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-gray-100 flex items-center gap-1"
            >
              <Download className="w-4 h-4" /> Install
            </button>
            <button onClick={dismissInstallBanner} className="p-1 hover:bg-white/20 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-[#404040] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <h1 className="text-xl font-bold">Harry & Jacobs</h1>
              
              {/* Department Switch */}
              <div className="hidden md:flex bg-[#333] rounded-lg p-1">
                <button
                  onClick={() => setActiveDepartment('estate')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    activeDepartment === 'estate' ? 'bg-[#FF8C42] text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Home className="w-4 h-4" /> Estate
                </button>
                <button
                  onClick={() => setActiveDepartment('architecture')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    activeDepartment === 'architecture' ? 'bg-[#FF8C42] text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Building2 className="w-4 h-4" /> Architecture
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/admin/tenancy-agreement">
                <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white font-semibold px-4">
                  <FileText className="w-4 h-4 mr-2" /> Create Tenancy Agreement
                </Button>
              </Link>
              <span className="text-sm text-gray-300 hidden md:block">Welcome, {adminName}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto p-4 md:p-6">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card 
            className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('listings')}
          >
            <CardContent className="p-4">
              <AlertCircle className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{pendingListings.length}</div>
              <div className="text-xs opacity-80">Pending Listings</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-green-500 to-green-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('listings')}
          >
            <CardContent className="p-4">
              <Building className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{activeListings.length}</div>
              <div className="text-xs opacity-80">Live Listings</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('employees')}
          >
            <CardContent className="p-4">
              <Users className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{filteredEmployees.filter(e => e.is_active !== false).length}</div>
              <div className="text-xs opacity-80">Team Members</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('tasks')}
          >
            <CardContent className="p-4">
              <ClipboardList className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{filteredTasks.filter(t => t.status !== 'completed').length}</div>
              <div className="text-xs opacity-80">Active Tasks</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-pink-500 to-pink-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('tenants')}
          >
            <CardContent className="p-4">
              <Banknote className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{tenants.filter(t => t.is_active).length}</div>
              <div className="text-xs opacity-80">Active Tenants</div>
            </CardContent>
          </Card>
          <Card 
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setActiveTab('leads')}
          >
            <CardContent className="p-4">
              <MessageSquare className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{filteredLeads.length}</div>
              <div className="text-xs opacity-80">New Leads</div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-white p-2 rounded-xl shadow-sm overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-[#FF8C42] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-red-500 text-white'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
          <Button onClick={fetchAllData} variant="ghost" size="sm" className="ml-auto" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mb-6 flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* ============== OVERVIEW TAB ============== */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Listings */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Pending Approval
                  </h3>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('listings')}>
                    View All
                  </Button>
                </div>
                {pendingListings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No pending listings</p>
                ) : (
                  <div className="space-y-3">
                    {pendingListings.slice(0, 4).map((listing) => (
                      <div key={listing.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{listing.title}</p>
                          <p className="text-xs text-gray-500">{listing.city} • £{listing.price?.toLocaleString()}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" className="bg-green-500 hover:bg-green-600 h-8 w-8 p-0" onClick={() => handleApproveListing(listing.id)}>
                            <CheckCheck className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600" onClick={() => handleRejectListing(listing.id)}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Tasks */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-[#FF8C42]" />
                    Active Tasks
                  </h3>
                  <Button size="sm" onClick={() => setShowModal('task')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                {filteredTasks.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No tasks</p>
                ) : (
                  <div className="space-y-3">
                    {filteredTasks.filter(t => t.status !== 'completed').slice(0, 4).map((task) => (
                      <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {task.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-green-500" /> :
                           task.status === 'in_progress' ? <Clock className="w-5 h-5 text-amber-500" /> :
                           <AlertCircle className="w-5 h-5 text-red-500" />}
                          <div>
                            <p className="font-medium text-sm">{task.title}</p>
                            <p className="text-xs text-gray-500">{task.assigned_by}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rent Collection Overview */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-green-500" />
                    Rent Collection
                  </h3>
                  <Button size="sm" onClick={() => setShowModal('tenant')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Plus className="w-4 h-4 mr-1" /> Add Tenant
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">{tenants.filter(t => t.payment_status === 'paid').length}</div>
                    <div className="text-xs text-gray-500">Paid</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <div className="text-xl font-bold text-amber-600">{tenants.filter(t => t.payment_status === 'pending').length}</div>
                    <div className="text-xs text-gray-500">Pending</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-xl font-bold text-red-600">{tenants.filter(t => t.payment_status === 'overdue').length}</div>
                    <div className="text-xs text-gray-500">Overdue</div>
                  </div>
                </div>
                {tenants.filter(t => t.payment_status !== 'paid').slice(0, 3).map(tenant => (
                  <div key={tenant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-2">
                    <div>
                      <p className="font-medium text-sm">{tenant.name}</p>
                      <p className="text-xs text-gray-500">£{tenant.monthly_rent} • Due: {tenant.next_due_date}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleSendRentReminder(tenant.id)}>
                      <Bell className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Recent Leads */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    New Leads
                  </h3>
                </div>
                {filteredLeads.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No new leads</p>
                ) : (
                  <div className="space-y-3">
                    {filteredLeads.slice(0, 4).map((lead, i) => (
                      <div key={lead.id || i} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{lead.name}</p>
                            <p className="text-xs text-gray-500">{lead.email}</p>
                            <p className="text-xs text-gray-500">{lead.property_address}</p>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">New</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============== LISTINGS TAB ============== */}
        {activeTab === 'listings' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold">Property Listings</h2>
                <p className="text-sm text-gray-500">
                  {pendingListings.length} pending • {activeListings.length} live • {listings.length} total
                </p>
              </div>
              <Button onClick={() => setShowModal('listing')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                <Plus className="w-4 h-4 mr-2" /> Add Listing
              </Button>
            </div>

            {/* Quick Filter Tabs */}
            <div className="flex gap-2 border-b pb-2">
              <button
                onClick={() => setFilterStatus('pending')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  filterStatus === 'pending' 
                    ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-500' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                🟡 Pending ({pendingListings.length})
              </button>
              <button
                onClick={() => setFilterStatus('active')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  filterStatus === 'active' 
                    ? 'bg-green-100 text-green-700 border-b-2 border-green-500' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                🟢 Live ({activeListings.length})
              </button>
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  filterStatus === 'all' 
                    ? 'bg-gray-200 text-gray-700 border-b-2 border-gray-500' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                All ({listings.length})
              </button>
            </div>

            {/* Pending Listings Section */}
            {(filterStatus === 'pending' || filterStatus === 'all') && pendingListings.length > 0 && (
              <div>
                <h3 className="font-bold text-lg mb-4 text-amber-600 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Pending Approval ({pendingListings.length})
                </h3>
                <div className="grid gap-4">
                  {searchFilter(pendingListings, ['title', 'address', 'city']).map((listing) => (
                    <Card key={listing.id} className="border-l-4 border-l-amber-500 shadow-lg">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex gap-4">
                            {listing.images?.[0] ? (
                              <img src={listing.images[0]} alt="" className="w-24 h-24 object-cover rounded-lg" />
                            ) : (
                              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                                <Image className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  {listing.listing_type === 'rent' ? 'To Rent' : 'For Sale'}
                                </span>
                                {listing.service_option === 'direct_contact' && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                    Direct Contact
                                  </span>
                                )}
                              </div>
                              <h4 className="font-bold text-lg">{listing.title}</h4>
                              <p className="text-gray-600">{listing.address}, {listing.city}</p>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                                <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {listing.bedrooms}</span>
                                <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {listing.bathrooms}</span>
                                <span className="text-[#FF8C42] font-bold">£{listing.price?.toLocaleString()}{listing.listing_type === 'rent' ? '/mo' : ''}</span>
                              </div>
                              {listing.landlord_name && (
                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                  <User className="w-3 h-3" /> Listed by: {listing.landlord_name}
                                </p>
                              )}
                              {listing.created_at && (
                                <p className="text-xs text-gray-400">
                                  Submitted: {new Date(listing.created_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 items-start">
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleEditListing(listing)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button className="bg-green-500 hover:bg-green-600" onClick={() => handleApproveListing(listing.id)}>
                              <CheckCheck className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button variant="outline" className="text-red-600 border-red-300" onClick={() => handleRejectListing(listing.id)}>
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {/* Active Listings */}
            {(filterStatus === 'active' || filterStatus === 'all') && (
              <div>
                <h3 className="font-bold text-lg mb-4 text-green-600 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Live Listings ({activeListings.length})
                </h3>
                {activeListings.length === 0 ? (
                  <Card className="shadow-lg">
                    <CardContent className="p-8 text-center text-gray-500">
                      <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No live listings yet</p>
                      <p className="text-sm mt-2">Approved listings will appear here</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchFilter(activeListings, ['title', 'address', 'city']).map((listing) => (
                      <Card key={listing.id} className="shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                        <div className="relative">
                          {listing.images?.[0] ? (
                            <img src={listing.images[0]} alt="" className="w-full h-40 object-cover" />
                          ) : (
                            <div className="w-full h-40 bg-gray-200 flex items-center justify-center">
                              <Image className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                          <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${
                            listing.listing_type === 'rent' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                          }`}>
                            {listing.listing_type === 'rent' ? 'To Rent' : 'For Sale'}
                          </span>
                          {listing.images?.length > 1 && (
                            <span className="absolute top-2 right-2 text-xs px-2 py-1 rounded-full bg-black/60 text-white">
                              {listing.images.length} photos
                            </span>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <h4 className="font-bold">{listing.title}</h4>
                          <p className="text-sm text-gray-600">{listing.city}, {listing.postcode}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {listing.bedrooms}</span>
                            <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {listing.bathrooms}</span>
                          </div>
                          {listing.landlord_name && (
                            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                              <User className="w-3 h-3" /> {listing.landlord_name}
                            </p>
                          )}
                          <div className="flex justify-between items-center mt-3 pt-3 border-t">
                            <span className="text-[#FF8C42] font-bold">£{listing.price?.toLocaleString()}{listing.listing_type === 'rent' ? '/mo' : ''}</span>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleEditListing(listing)} data-testid={`edit-listing-${listing.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => handleDeleteListing(listing.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============== AGENCY AGREEMENTS TAB ============== */}
        {activeTab === 'agreements' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold">Agency Agreements</h2>
                <p className="text-sm text-gray-500">
                  {pendingAgreements.length} pending signature • {agencyAgreements.filter(a => a.status === 'signed').length} signed
                </p>
              </div>
            </div>

            {/* Pending Signatures */}
            {pendingAgreements.length > 0 && (
              <div>
                <h3 className="font-bold text-lg mb-4 text-amber-600 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Awaiting Landlord Signature ({pendingAgreements.length})
                </h3>
                <div className="grid gap-4">
                  {pendingAgreements.map((agreement) => (
                    <Card key={agreement.id} className="border-l-4 border-l-amber-500 shadow-lg" data-testid={`agreement-${agreement.id}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                agreement.agreement_type === 'full_management' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {agreement.agreement_type === 'full_management' ? 'Full Management' : 'Tenant Finding Only'}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                Pending Signature
                              </span>
                            </div>
                            <h4 className="font-bold text-lg">{agreement.property_address}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                              <div>
                                <p className="text-xs text-gray-500">Landlord</p>
                                <p className="font-medium">{agreement.landlord_name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Email</p>
                                <a href={`mailto:${agreement.landlord_email}`} className="text-blue-600 hover:underline text-sm">
                                  {agreement.landlord_email}
                                </a>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Phone</p>
                                <a href={`tel:${agreement.landlord_phone}`} className="font-medium">
                                  {agreement.landlord_phone || 'N/A'}
                                </a>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Expected Rent</p>
                                <p className="font-bold text-[#FF8C42]">£{agreement.expected_rent?.toLocaleString()}/mo</p>
                              </div>
                            </div>
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-gray-500">Fee Structure</p>
                                  {agreement.agreement_type === 'full_management' ? (
                                    <p className="font-medium text-green-700">12% Commission (£{((agreement.expected_rent || 0) * 0.12).toFixed(0)}/mo)</p>
                                  ) : (
                                    <p className="font-medium text-blue-700">One-time: £{agreement.tenant_find_fee?.toLocaleString()} (55% first month)</p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Sent On</p>
                                  <p className="text-sm">{new Date(agreement.created_at).toLocaleDateString()}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button 
                              onClick={() => handleMarkAgreementSigned(agreement.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`sign-agreement-${agreement.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Mark as Signed
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(`mailto:${agreement.landlord_email}?subject=Agency Agreement Reminder - ${agreement.property_address}`, '_blank')}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Send Reminder
                            </Button>
                            <a 
                              href={`https://wa.me/${(agreement.landlord_phone || '').replace(/\D/g, '')}?text=Hi ${agreement.landlord_name}, this is Harry %26 Jacobs. Just following up on the Agency Agreement for ${agreement.property_address}. Please sign and return at your earliest convenience.`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-600 hover:underline text-center"
                            >
                              WhatsApp Reminder
                            </a>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Signed Agreements */}
            {agencyAgreements.filter(a => a.status === 'signed').length > 0 && (
              <div>
                <h3 className="font-bold text-lg mb-4 text-green-600 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Signed Agreements ({agencyAgreements.filter(a => a.status === 'signed').length})
                </h3>
                <div className="grid gap-3">
                  {agencyAgreements.filter(a => a.status === 'signed').map((agreement) => (
                    <Card key={agreement.id} className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium">{agreement.property_address}</h4>
                            <p className="text-sm text-gray-600">{agreement.landlord_name} • {agreement.agreement_type === 'full_management' ? 'Full Management' : 'Tenant Finding'}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Signed</span>
                            {agreement.signed_at && (
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(agreement.signed_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {agencyAgreements.length === 0 && (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No agency agreements yet</p>
                  <p className="text-sm mt-2">Agreements are automatically generated when landlords submit new listings.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ============== VIEWING REQUESTS TAB ============== */}
        {activeTab === 'viewings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Property Viewing Requests</h2>
              <div className="text-sm text-gray-500">
                {viewingRequests.filter(v => v.status === 'pending').length} pending
              </div>
            </div>

            {viewingRequests.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <CalendarCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No viewing requests yet</p>
                  <p className="text-sm mt-2">Viewing requests will appear here when potential buyers/tenants book viewings</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Viewings */}
                {viewingRequests.filter(v => v.status === 'pending').length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-4 text-amber-600">
                      Pending Approval ({viewingRequests.filter(v => v.status === 'pending').length})
                    </h3>
                    <div className="grid gap-4">
                      {viewingRequests.filter(v => v.status === 'pending').map((viewing) => (
                        <Card key={viewing.id} className="shadow-lg border-l-4 border-l-amber-500">
                          <CardContent className="p-4">
                            <div className="flex flex-col lg:flex-row justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(viewing.status)}`}>
                                    {viewing.status}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(viewing.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <h4 className="font-bold text-lg">{viewing.property_title}</h4>
                                <p className="text-gray-600 text-sm">{viewing.property_address}</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                                  <div>
                                    <p className="text-gray-500 text-xs">Client</p>
                                    <p className="font-medium">{viewing.name}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Contact</p>
                                    <p>{viewing.phone}</p>
                                    {viewing.email && <p className="text-xs text-gray-500">{viewing.email}</p>}
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Preferred Date</p>
                                    <p className="font-medium text-[#FF8C42]">{viewing.preferred_date}</p>
                                    {viewing.preferred_time && <p className="text-xs">{viewing.preferred_time}</p>}
                                  </div>
                                  {viewing.message && (
                                    <div>
                                      <p className="text-gray-500 text-xs">Notes</p>
                                      <p className="text-xs">{viewing.message}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <select
                                  className="p-2 border rounded-lg text-sm"
                                  id={`agent-select-${viewing.id}`}
                                  defaultValue=""
                                >
                                  <option value="">Select Agent</option>
                                  {filteredEmployees.filter(e => e.is_active !== false && e.invitation_accepted).map(emp => (
                                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                                  ))}
                                </select>
                                <Button 
                                  className="bg-green-500 hover:bg-green-600" 
                                  onClick={() => {
                                    const agentId = document.getElementById(`agent-select-${viewing.id}`).value;
                                    if (!agentId) {
                                      toast({ title: "Please select an agent", variant: "destructive" });
                                      return;
                                    }
                                    handleApproveViewing(viewing.id, agentId);
                                  }}
                                >
                                  <CheckCheck className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button 
                                  variant="outline" 
                                  className="text-red-600 border-red-300" 
                                  onClick={() => handleRejectViewing(viewing.id)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved/Completed Viewings */}
                {viewingRequests.filter(v => v.status !== 'pending').length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-4 text-gray-600">
                      Processed ({viewingRequests.filter(v => v.status !== 'pending').length})
                    </h3>
                    <div className="grid gap-4">
                      {viewingRequests.filter(v => v.status !== 'pending').map((viewing) => (
                        <Card key={viewing.id} className={`shadow-lg border-l-4 ${
                          viewing.status === 'approved' ? 'border-l-green-500' : 
                          viewing.status === 'completed' ? 'border-l-blue-500' : 'border-l-red-500'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(viewing.status)}`}>
                                    {viewing.status}
                                  </span>
                                </div>
                                <h4 className="font-bold">{viewing.property_title}</h4>
                                <p className="text-sm text-gray-600">{viewing.name} • {viewing.preferred_date}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============== CONSULTATION REQUESTS TAB ============== */}
        {activeTab === 'consultations' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Architecture Consultation Requests</h2>
              <div className="text-sm text-gray-500">
                {consultationRequests.filter(c => c.status === 'pending').length} pending
              </div>
            </div>

            {consultationRequests.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No consultation requests yet</p>
                  <p className="text-sm mt-2">Consultation requests will appear here when clients book architecture consultations</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Pending Consultations */}
                {consultationRequests.filter(c => c.status === 'pending').length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-4 text-amber-600">
                      Pending Approval ({consultationRequests.filter(c => c.status === 'pending').length})
                    </h3>
                    <div className="grid gap-4">
                      {consultationRequests.filter(c => c.status === 'pending').map((consult) => (
                        <Card key={consult.id} className="shadow-lg border-l-4 border-l-amber-500">
                          <CardContent className="p-4">
                            <div className="flex flex-col lg:flex-row justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(consult.status)}`}>
                                    {consult.status}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                    {consult.project_type}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(consult.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <h4 className="font-bold text-lg capitalize">{consult.project_type.replace('_', ' ')} Project</h4>
                                {consult.property_address && (
                                  <p className="text-gray-600 text-sm">{consult.property_address}</p>
                                )}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                                  <div>
                                    <p className="text-gray-500 text-xs">Client</p>
                                    <p className="font-medium">{consult.name}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Contact</p>
                                    <p>{consult.phone}</p>
                                    {consult.email && <p className="text-xs text-gray-500">{consult.email}</p>}
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Preferred Date</p>
                                    <p className="font-medium text-[#FF8C42]">{consult.preferred_date}</p>
                                    {consult.preferred_time && <p className="text-xs">{consult.preferred_time}</p>}
                                  </div>
                                  {consult.budget_range && (
                                    <div>
                                      <p className="text-gray-500 text-xs">Budget</p>
                                      <p className="font-medium">{consult.budget_range}</p>
                                    </div>
                                  )}
                                </div>
                                {consult.message && (
                                  <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                                    <p className="text-gray-500 text-xs mb-1">Project Description</p>
                                    <p>{consult.message}</p>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2">
                                <select
                                  className="p-2 border rounded-lg text-sm"
                                  id={`architect-select-${consult.id}`}
                                  defaultValue=""
                                >
                                  <option value="">Select Architect</option>
                                  {employees.filter(e => e.department === 'architecture' && e.is_active !== false && e.invitation_accepted).map(emp => (
                                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                                  ))}
                                </select>
                                <Button 
                                  className="bg-green-500 hover:bg-green-600" 
                                  onClick={() => {
                                    const architectId = document.getElementById(`architect-select-${consult.id}`).value;
                                    if (!architectId) {
                                      toast({ title: "Please select an architect", variant: "destructive" });
                                      return;
                                    }
                                    handleApproveConsultation(consult.id, architectId);
                                  }}
                                >
                                  <CheckCheck className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button 
                                  variant="outline" 
                                  className="text-red-600 border-red-300" 
                                  onClick={() => handleRejectConsultation(consult.id)}
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processed Consultations */}
                {consultationRequests.filter(c => c.status !== 'pending').length > 0 && (
                  <div>
                    <h3 className="font-bold text-lg mb-4 text-gray-600">
                      Processed ({consultationRequests.filter(c => c.status !== 'pending').length})
                    </h3>
                    <div className="grid gap-4">
                      {consultationRequests.filter(c => c.status !== 'pending').map((consult) => (
                        <Card key={consult.id} className={`shadow-lg border-l-4 ${
                          consult.status === 'approved' ? 'border-l-green-500' : 
                          consult.status === 'completed' ? 'border-l-blue-500' : 'border-l-red-500'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(consult.status)}`}>
                                    {consult.status}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                    {consult.project_type}
                                  </span>
                                </div>
                                <h4 className="font-bold">{consult.name}</h4>
                                <p className="text-sm text-gray-600">{consult.preferred_date}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============== EMPLOYEES TAB ============== */}
        {activeTab === 'employees' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Team Management</h2>
              <Button onClick={() => setShowModal('invite')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                <Send className="w-4 h-4 mr-2" /> Invite Employee
              </Button>
            </div>

            {/* Role definitions */}
            {[
              { role: 'admin', label: 'Administrators', color: 'bg-purple-500', icon: '👑' },
              { role: 'manager', label: 'Managers', color: 'bg-blue-500', icon: '📊' },
              { role: 'lettings_agent', label: 'Lettings Agents', color: 'bg-green-500', icon: '🏠' },
              { role: 'sales_agent', label: 'Sales Agents', color: 'bg-orange-500', icon: '💰' },
              { role: 'architectural_designer', label: 'Architectural Designers', color: 'bg-cyan-500', icon: '📐' }
            ].map(roleGroup => {
              const roleEmployees = searchFilter(filteredEmployees, ['name', 'email']).filter(emp => emp.role === roleGroup.role);
              if (roleEmployees.length === 0) return null;
              
              return (
                <div key={roleGroup.role} className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">{roleGroup.icon}</span>
                    <h3 className="font-bold text-lg text-[#404040]">{roleGroup.label}</h3>
                    <span className={`${roleGroup.color} text-white text-xs px-2 py-0.5 rounded-full`}>
                      {roleEmployees.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {roleEmployees.map((emp) => (
                      <Card key={emp.id} className="shadow-lg hover:shadow-xl transition-shadow border-l-4" style={{ borderLeftColor: roleGroup.color.replace('bg-', '').replace('-500', '') === 'purple' ? '#8b5cf6' : roleGroup.color.replace('bg-', '').replace('-500', '') === 'blue' ? '#3b82f6' : roleGroup.color.replace('bg-', '').replace('-500', '') === 'green' ? '#22c55e' : roleGroup.color.replace('bg-', '').replace('-500', '') === 'orange' ? '#f97316' : '#06b6d4' }}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex gap-3">
                              <div className={`w-12 h-12 rounded-full ${roleGroup.color}/10 flex items-center justify-center`}>
                                <User className={`w-6 h-6 ${roleGroup.color.replace('bg-', 'text-')}`} />
                              </div>
                              <div>
                                <h4 className="font-bold">{emp.name || 'Pending Setup'}</h4>
                                <p className="text-sm text-gray-600">{emp.email}</p>
                                {emp.phone && <p className="text-sm text-gray-500">{emp.phone}</p>}
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    emp.invitation_accepted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {emp.invitation_accepted ? 'Active' : 'Pending Setup'}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                    {emp.department === 'architecture' ? 'Architecture' : 'Estate'}
                                  </span>
                                </div>
                                {/* Copy Setup Link for pending invitations */}
                                {!emp.invitation_accepted && emp.invitation_token && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="mt-2 text-xs"
                                    onClick={() => {
                                      const setupLink = `${window.location.origin}/employee/setup?token=${emp.invitation_token}`;
                                      navigator.clipboard.writeText(setupLink);
                                      toast({ title: "Link Copied!", description: "Setup link copied to clipboard. Share it with the employee." });
                                    }}
                                  >
                                    <Copy className="w-3 h-3 mr-1" /> Copy Setup Link
                                  </Button>
                                )}
                                {/* Enter Employee Dashboard - Admin Access (for any employee) */}
                                <Button 
                                  size="sm" 
                                  className="mt-2 bg-blue-500 hover:bg-blue-600 text-white text-xs"
                                  onClick={() => {
                                    // Store admin impersonation data
                                    localStorage.setItem('adminImpersonating', 'true');
                                    localStorage.setItem('impersonatedEmployeeId', emp.id);
                                    localStorage.setItem('impersonatedEmployeeName', emp.name || emp.email);
                                    localStorage.setItem('impersonatedEmployeeEmail', emp.email);
                                    localStorage.setItem('impersonatedEmployeeDept', emp.department);
                                    // Open employee dashboard in new tab
                                    window.open('/employee/dashboard?admin_view=true', '_blank');
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Enter Dashboard
                                </Button>
                              </div>
                            </div>
                            {emp.is_active !== false && (
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeactivateEmployee(emp.id)}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Show message if no employees */}
            {filteredEmployees.length === 0 && (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No team members yet</p>
                  <p className="text-sm mt-2">Click "Invite Employee" to add your first team member.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ============== TASKS TAB ============== */}
        {activeTab === 'tasks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Task Management</h2>
              <Button onClick={() => setShowModal('task')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                <Plus className="w-4 h-4 mr-2" /> Create Task
              </Button>
            </div>

            <div className="space-y-3">
              {searchFilter(filteredTasks, ['title', 'description']).map((task) => (
                <Card key={task.id} className={`shadow-lg border-l-4 ${
                  task.status === 'completed' ? 'border-l-green-500' :
                  task.status === 'in_progress' ? 'border-l-amber-500' : 'border-l-red-500'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {task.status === 'completed' ? <CheckCircle2 className="w-6 h-6 text-green-500 mt-1" /> :
                         task.status === 'in_progress' ? <Clock className="w-6 h-6 text-amber-500 mt-1" /> :
                         <AlertCircle className="w-6 h-6 text-red-500 mt-1" />}
                        <div>
                          <h4 className="font-bold">{task.title}</h4>
                          <p className="text-sm text-gray-600">{task.description}</p>
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                            <span>Assigned by: {task.assigned_by}</span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>{task.priority}</span>
                            {task.due_date && <span>Due: {task.due_date}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <div className="flex gap-1">
                          <Button size="sm" variant={task.status === 'pending' ? 'default' : 'outline'}
                            className={task.status === 'pending' ? 'bg-red-500 hover:bg-red-600' : ''}
                            onClick={() => handleTaskStatus(task.id, 'pending')}>
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant={task.status === 'in_progress' ? 'default' : 'outline'}
                            className={task.status === 'in_progress' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                            onClick={() => handleTaskStatus(task.id, 'in_progress')}>
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant={task.status === 'completed' ? 'default' : 'outline'}
                            className={task.status === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
                            onClick={() => handleTaskStatus(task.id, 'completed')}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ============== TENANTS/RENT TAB ============== */}
        {activeTab === 'tenants' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Rent Collection</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={async () => {
                  await fetch(`${API_URL}/api/rent/send-bulk-reminders`, { method: 'POST' });
                  toast({ title: "Bulk Reminders Sent" });
                }}>
                  <Bell className="w-4 h-4 mr-2" /> Send All Reminders
                </Button>
                <Button onClick={() => { resetTenantForm(); setShowModal('tenant'); }} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                  <Plus className="w-4 h-4 mr-2" /> Add Tenant
                </Button>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500 mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    placeholder="Search by name, address, email, landlord..." 
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Filter by Status</Label>
                <select 
                  value={tenantFilter} 
                  onChange={(e) => setTenantFilter(e.target.value)}
                  className="p-2 border rounded-md min-w-[150px]"
                >
                  <option value="all">All Tenants</option>
                  <option value="paid">✓ Paid</option>
                  <option value="pending">✗ Not Paid</option>
                  <option value="vacant">◯ Vacant</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Sort by</Label>
                <select 
                  value={tenantSort} 
                  onChange={(e) => setTenantSort(e.target.value)}
                  className="p-2 border rounded-md min-w-[150px]"
                >
                  <option value="name">Name (A-Z)</option>
                  <option value="address">Address (A-Z)</option>
                  <option value="rent">Rent (High-Low)</option>
                  <option value="due_date">Due Date (1st-28th)</option>
                  <option value="status">Status (Overdue First)</option>
                </select>
              </div>
            </div>

            {/* Profit Summary - Internal Only */}
            <Card className="mb-6 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" /> Agency Profit Summary (Internal)
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">
                      £{tenants.filter(t => t.is_active !== false).reduce((sum, t) => sum + (parseFloat(t.rent_collected) || parseFloat(t.monthly_rent) || 0), 0).toLocaleString()}
                    </div>
                    <div className="text-sm opacity-80">Rent Collected</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">
                      £{tenants.filter(t => t.is_active !== false).reduce((sum, t) => sum + (parseFloat(t.rent_forwarded) || 0), 0).toLocaleString()}
                    </div>
                    <div className="text-sm opacity-80">Forwarded to Landlords</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-300">
                      £{tenants.filter(t => t.is_active !== false).reduce((sum, t) => {
                        const collected = parseFloat(t.rent_collected) || parseFloat(t.monthly_rent) || 0;
                        const forwarded = parseFloat(t.rent_forwarded) || 0;
                        return sum + (collected - forwarded);
                      }, 0).toLocaleString()}
                    </div>
                    <div className="text-sm opacity-80">Monthly Profit</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-300">
                      £{(tenants.filter(t => t.is_active !== false).reduce((sum, t) => {
                        const collected = parseFloat(t.rent_collected) || parseFloat(t.monthly_rent) || 0;
                        const forwarded = parseFloat(t.rent_forwarded) || 0;
                        return sum + (collected - forwarded);
                      }, 0) * 12).toLocaleString()}
                    </div>
                    <div className="text-sm opacity-80">Annual Profit</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{tenants.filter(t => t.payment_status === 'paid' && t.is_active !== false).length}</div>
                  <div className="text-sm text-green-700">Paid</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{tenants.filter(t => (t.payment_status === 'pending' || !t.payment_status) && t.is_active !== false).length}</div>
                  <div className="text-sm text-red-700">Not Paid</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{tenants.filter(t => t.payment_status === 'vacant' && t.is_active !== false).length}</div>
                  <div className="text-sm text-orange-700">Vacant</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    £{tenants.filter(t => t.is_active !== false).reduce((sum, t) => sum + (t.monthly_rent || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-700">Total Monthly</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4">
              {getFilteredTenants().map((tenant) => (
                <Card 
                  key={tenant.id} 
                  className={`shadow-lg border-l-4 cursor-pointer hover:shadow-xl transition-shadow ${
                    tenant.payment_status === 'paid' ? 'border-l-green-500' :
                    tenant.payment_status === 'vacant' ? 'border-l-orange-500' : 'border-l-red-500'
                  }`}
                  onClick={() => openTenantDetail(tenant)}
                  data-testid={`tenant-card-${tenant.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-bold text-lg">{tenant.name}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusBadge(tenant.payment_status)}`}>
                            {getPaymentStatusLabel(tenant.payment_status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{tenant.email} • {tenant.phone}</p>
                        <p className="text-sm text-gray-500 mt-1">{tenant.property_address}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm">
                          <span className="font-bold text-[#FF8C42]">£{tenant.monthly_rent?.toLocaleString()}/mo</span>
                          <span>Due: {tenant.rent_due_day || 1}th</span>
                          {tenant.reminders_sent > 0 && (
                            <span className="text-red-500 font-medium">
                              {tenant.reminders_sent} reminder{tenant.reminders_sent > 1 ? 's' : ''} sent
                              {tenant.last_reminder_date && ` (last: ${tenant.last_reminder_date})`}
                            </span>
                          )}
                          {tenant.last_payment_date && <span className="text-green-600">Last paid: {tenant.last_payment_date}</span>}
                          {tenant.deposit_scheme && <span className="text-blue-600">Deposit: {tenant.deposit_scheme}</span>}
                          {tenant.landlord_name && <span className="text-purple-600">Landlord: {tenant.landlord_name}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 items-end" onClick={(e) => e.stopPropagation()}>
                        {/* E-Signature Section */}
                        <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                          <span className="text-xs text-amber-700 font-medium">E-Sign:</span>
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getSignatureStatusBadge(tenant.signature_status)}`}>
                            {getSignatureStatusLabel(tenant.signature_status)}
                          </span>
                          {tenant.signature_status !== 'signed' && (
                            <Button 
                              size="sm" 
                              className="bg-amber-500 hover:bg-amber-600 text-white shadow-sm h-7"
                              onClick={() => handleSendForSignature(tenant)}
                              disabled={sendingSignature === tenant.id}
                              title="Send Tenancy Agreement for E-Signature"
                              data-testid={`send-signature-${tenant.id}`}
                            >
                              {sendingSignature === tenant.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <PenTool className="w-3 h-3 mr-1" /> Send for Signature
                                </>
                              )}
                            </Button>
                          )}
                          {tenant.signature_status === 'signed' && tenant.signature_request_id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-green-600 border-green-300 h-7"
                              onClick={() => window.open(`${API_URL}/api/signature/${tenant.signature_request_id}/download`, '_blank')}
                              title="Download Signed Agreement"
                            >
                              <Download className="w-3 h-3 mr-1" /> Download
                            </Button>
                          )}
                        </div>
                        {/* Document Generation Section */}
                        <div className="flex gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                          <span className="text-xs text-gray-500 font-medium self-center mr-1">Documents:</span>
                          <Button 
                            size="sm" 
                            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                            onClick={() => downloadDocument(
                              `${API_URL}/api/tenants/${tenant.id}/generate-tenancy-agreement`,
                              `Tenancy_Agreement_${tenant.name?.replace(/\s+/g, '_') || 'Tenant'}.pdf`
                            )}
                            title="Download Tenancy Agreement PDF"
                            data-testid={`generate-tenancy-${tenant.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> Tenancy Agreement
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                            onClick={() => downloadDocument(
                              `${API_URL}/api/tenants/${tenant.id}/generate-landlord-letter`,
                              `Landlord_Letter_${tenant.name?.replace(/\s+/g, '_') || 'Tenant'}.pdf`
                            )}
                            title="Download Landlord Letter for Universal Credit"
                            data-testid={`generate-letter-${tenant.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> Landlord Letter
                          </Button>
                        </div>
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleSendRentReminder(tenant.id, true, false)} title="Send Email Only">
                            <Send className="w-4 h-4 mr-1" /> Email
                          </Button>
                          <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleSendRentReminder(tenant.id, false, true)} title="Send WhatsApp Only">
                            <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleCreateRentChaseTask(tenant)} title="Assign to Agent">
                            <UserPlus className="w-4 h-4 mr-1" /> Assign
                          </Button>
                          <select
                            className={`h-8 px-3 text-sm font-medium rounded-md border cursor-pointer ${
                              tenant.payment_status === 'paid' ? 'bg-green-500 text-white border-green-600' :
                              tenant.payment_status === 'vacant' ? 'bg-orange-500 text-white border-orange-600' :
                              'bg-red-500 text-white border-red-600'
                            }`}
                            value={tenant.payment_status || 'pending'}
                            onChange={(e) => handleUpdatePaymentStatus(tenant.id, e.target.value)}
                            data-testid={`payment-status-${tenant.id}`}
                          >
                            <option value="paid" className="bg-white text-green-600">✓ PAID</option>
                            <option value="pending" className="bg-white text-red-600">✗ NOT PAID</option>
                            <option value="vacant" className="bg-white text-orange-600">◯ VACANT</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {getFilteredTenants().length === 0 && (
                <Card className="bg-gray-50">
                  <CardContent className="p-8 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">{tenantSearch || tenantFilter !== 'all' ? 'No matching tenants' : 'No tenants yet'}</p>
                    <p className="text-sm">{tenantSearch || tenantFilter !== 'all' ? 'Try adjusting your search or filter' : 'Click "Add Tenant" to add your first tenant'}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* ============== LEADS TAB ============== */}
        {activeTab === 'leads' && (
          <div>
            <h2 className="text-xl font-bold mb-6">
              {activeDepartment === 'architecture' ? 'Architecture' : 'Landlord'} Leads ({filteredLeads.length})
            </h2>
            <div className="grid gap-4">
              {searchFilter(filteredLeads, ['name', 'email', 'property_address']).map((lead, i) => (
                <Card key={lead.id || i} className="shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h4 className="font-bold">{lead.name}</h4>
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">New</span>
                        </div>
                        <p className="text-sm text-gray-600">{lead.email} • {lead.phone}</p>
                        <p className="text-sm text-gray-500 mt-1">{lead.property_address}, {lead.postcode}</p>
                        {lead.project_type && <p className="text-sm text-[#FF8C42] mt-1">Project: {lead.project_type}</p>}
                        {lead.property_type && <p className="text-sm text-[#FF8C42] mt-1">Property: {lead.property_type}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Phone className="w-4 h-4 mr-1" /> Call
                        </Button>
                        <Button size="sm" variant="outline">
                          <Mail className="w-4 h-4 mr-1" /> Email
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ============== BLOG TAB ============== */}
        {activeTab === 'blog' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Blog Management</h2>
              <Button className="bg-[#FF8C42] hover:bg-[#FF6B35]" onClick={() => {
                setBlogForm({
                  title: '', slug: '', excerpt: '', content: '', category: 'landlord',
                  image: '', tags: '', featured: false, published: false, meta_title: '', meta_description: ''
                });
                setEditingBlog(null);
                setShowModal('blog');
              }}>
                <Plus className="w-4 h-4 mr-2" /> New Blog Post
              </Button>
            </div>

            {/* Blog Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{blogPosts.length}</div>
                  <div className="text-sm text-blue-700">Total Posts</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{blogPosts.filter(b => b.published).length}</div>
                  <div className="text-sm text-green-700">Published</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{blogPosts.filter(b => !b.published).length}</div>
                  <div className="text-sm text-amber-700">Drafts</div>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{blogPosts.filter(b => b.featured).length}</div>
                  <div className="text-sm text-purple-700">Featured</div>
                </CardContent>
              </Card>
            </div>

            {blogPosts.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No blog posts yet. Create your first post!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {blogPosts.map((post) => (
                  <Card key={post.id} className={`shadow-lg border-l-4 ${post.published ? 'border-l-green-500' : 'border-l-amber-500'}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {post.image && (
                          <img src={post.image} alt={post.title} className="w-32 h-24 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-lg">{post.title}</h3>
                            {post.featured && <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">Featured</span>}
                            <span className={`text-xs px-2 py-1 rounded ${post.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {post.published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{post.excerpt}</p>
                          <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded capitalize">{post.category}</span>
                            {post.tags?.map(tag => (
                              <span key={tag} className="bg-blue-50 text-blue-600 px-2 py-1 rounded">{tag}</span>
                            ))}
                            <span>/{post.slug}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditBlog(post)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className={post.published ? 'text-amber-600' : 'text-green-600'}
                            onClick={() => handlePublishBlog(post.id, !post.published)}
                          >
                            {post.published ? <Eye className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="outline" className="text-blue-600" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteBlog(post.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============== SEO TAB ============== */}
        {activeTab === 'seo' && (
          <div>
            <h2 className="text-xl font-bold mb-6">SEO Settings</h2>
            
            <div className="grid gap-6">
              {/* Global SEO Settings */}
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-[#FF8C42]" /> Global Settings
                  </h3>
                  <div className="grid gap-4">
                    <div>
                      <Label>Site Title</Label>
                      <Input 
                        value={seoSettings.site_title || ''} 
                        onChange={(e) => setSeoSettings({...seoSettings, site_title: e.target.value})}
                        placeholder="Harry & Jacobs | Estate Agents in Harrow"
                      />
                    </div>
                    <div>
                      <Label>Site Description (Meta)</Label>
                      <Textarea 
                        value={seoSettings.site_description || ''} 
                        onChange={(e) => setSeoSettings({...seoSettings, site_description: e.target.value})}
                        placeholder="Award-winning estate agents in Harrow..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Google Analytics ID</Label>
                      <Input 
                        value={seoSettings.google_analytics_id || ''} 
                        onChange={(e) => setSeoSettings({...seoSettings, google_analytics_id: e.target.value})}
                        placeholder="G-XXXXXXXXXX"
                      />
                    </div>
                    <div>
                      <Label>Keywords (comma separated)</Label>
                      <Input 
                        value={seoSettings.keywords?.join(', ') || ''} 
                        onChange={(e) => setSeoSettings({...seoSettings, keywords: e.target.value.split(',').map(k => k.trim())})}
                        placeholder="estate agents harrow, letting agents, property management"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button className="bg-[#FF8C42] hover:bg-[#FF6B35]" onClick={handleSaveSeoSettings}>
                      <Save className="w-4 h-4 mr-2" /> Save SEO Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* SEO Quick Actions */}
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Button variant="outline" onClick={() => window.open('/sitemap.xml', '_blank')}>
                      <FileText className="w-4 h-4 mr-2" /> View Sitemap
                    </Button>
                    <Button variant="outline" onClick={() => window.open('/robots.txt', '_blank')}>
                      <FileText className="w-4 h-4 mr-2" /> View Robots.txt
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://search.google.com/search-console', '_blank')}>
                      <Search className="w-4 h-4 mr-2" /> Google Console
                    </Button>
                    <Button variant="outline" onClick={() => window.open('https://analytics.google.com', '_blank')}>
                      <TrendingUp className="w-4 h-4 mr-2" /> Analytics
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* SEO Checklist */}
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">SEO Checklist</h3>
                  <div className="grid gap-2">
                    {[
                      { done: true, label: "Sitemap.xml created" },
                      { done: true, label: "Robots.txt configured" },
                      { done: true, label: "Google Analytics installed" },
                      { done: blogPosts.filter(b => b.published).length >= 10, label: "10+ blog posts published" },
                      { done: true, label: "Local area landing pages created" },
                      { done: !!seoSettings.site_title, label: "Site title configured" },
                      { done: !!seoSettings.site_description, label: "Meta description set" },
                    ].map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-2 p-2 rounded ${item.done ? 'bg-green-50' : 'bg-amber-50'}`}>
                        {item.done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
                        <span className={item.done ? 'text-green-700' : 'text-amber-700'}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ============== UPLOADS TAB ============== */}
        {activeTab === 'uploads' && (
          <div>
            <h2 className="text-xl font-bold mb-6">Portal Uploads</h2>
            {filteredUploads.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  No uploads to review
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredUploads.map((upload) => (
                  <Card key={upload.id} className={`shadow-lg border-l-4 ${
                    upload.status === 'approved' ? 'border-l-green-500' :
                    upload.status === 'rejected' ? 'border-l-red-500' : 'border-l-amber-500'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                            {upload.file_type === 'image' ? (
                              <img src={`${API_URL}${upload.file_url}`} alt="" className="w-full h-full object-cover" />
                            ) : upload.file_type === 'video' ? (
                              <Video className="w-8 h-8 text-gray-400" />
                            ) : (
                              <File className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-bold">{upload.file_name}</h4>
                            <p className="text-sm text-gray-600">By: {upload.uploaded_by_name}</p>
                            <p className="text-sm text-gray-500">{upload.description}</p>
                            <span className={`text-xs px-2 py-1 rounded-full mt-2 inline-block ${getStatusBadge(upload.status)}`}>
                              {upload.status}
                            </span>
                          </div>
                        </div>
                        {upload.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => handleApproveUpload(upload.id)}>
                              <CheckCheck className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectUpload(upload.id)}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============== PROPERTY SEEKERS TAB ============== */}
        {activeTab === 'seekers' && (
          <div>
            {/* Header with Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none">
                <CardContent className="p-4">
                  <Home className="w-6 h-6 mb-2 opacity-80" />
                  <div className="text-2xl font-bold">{propertySeekers.filter(s => s.looking_for === 'rent').length}</div>
                  <div className="text-xs opacity-80">Looking to Rent</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-none">
                <CardContent className="p-4">
                  <PoundSterling className="w-6 h-6 mb-2 opacity-80" />
                  <div className="text-2xl font-bold">{propertySeekers.filter(s => s.looking_for === 'sale').length}</div>
                  <div className="text-xs opacity-80">Looking to Buy</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-none">
                <CardContent className="p-4">
                  <AlertCircle className="w-6 h-6 mb-2 opacity-80" />
                  <div className="text-2xl font-bold">{propertySeekers.filter(s => s.status === 'new').length}</div>
                  <div className="text-xs opacity-80">New (Uncontacted)</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-none">
                <CardContent className="p-4">
                  <CheckCircle2 className="w-6 h-6 mb-2 opacity-80" />
                  <div className="text-2xl font-bold">{propertySeekers.filter(s => s.assigned_to).length}</div>
                  <div className="text-xs opacity-80">Assigned</div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card className="shadow-lg mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Type</Label>
                    <select 
                      className="p-2 border rounded-lg text-sm min-w-[120px]"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="rent">🏠 Rent Only</option>
                      <option value="sale">💰 Buy Only</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
                    <select 
                      className="p-2 border rounded-lg text-sm min-w-[140px]"
                      id="statusFilter"
                      defaultValue="all"
                    >
                      <option value="all">All Status</option>
                      <option value="new">🟢 New</option>
                      <option value="contacted">🔵 Contacted</option>
                      <option value="viewing_scheduled">🟡 Viewing</option>
                      <option value="offer_made">🟠 Offer Made</option>
                      <option value="closed">⚫ Closed</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Bedrooms</Label>
                    <select 
                      className="p-2 border rounded-lg text-sm min-w-[100px]"
                      id="bedroomFilter"
                      defaultValue="all"
                    >
                      <option value="all">Any</option>
                      <option value="0">Studio</option>
                      <option value="1">1 Bed</option>
                      <option value="2">2 Beds</option>
                      <option value="3">3 Beds</option>
                      <option value="4">4+ Beds</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Budget Range</Label>
                    <select 
                      className="p-2 border rounded-lg text-sm min-w-[140px]"
                      id="budgetFilter"
                      defaultValue="all"
                    >
                      <option value="all">Any Budget</option>
                      <option value="0-1000">£0 - £1,000</option>
                      <option value="1000-1500">£1,000 - £1,500</option>
                      <option value="1500-2000">£1,500 - £2,000</option>
                      <option value="2000-3000">£2,000 - £3,000</option>
                      <option value="3000+">£3,000+</option>
                    </select>
                  </div>
                  <div className="ml-auto">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        document.getElementById('statusFilter').value = 'all';
                        document.getElementById('bedroomFilter').value = 'all';
                        document.getElementById('budgetFilter').value = 'all';
                        setFilterStatus('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {propertySeekers.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <UserSearch className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No property seekers yet</p>
                  <p className="text-sm mt-2">Share your property search page to collect leads:</p>
                  <code className="text-xs bg-gray-100 px-3 py-2 rounded mt-2 inline-block text-[#FF8C42]">
                    {window.location.origin}/find-property
                  </code>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Rent Section */}
                {(filterStatus === 'all' || filterStatus === 'rent') && propertySeekers.filter(s => s.looking_for === 'rent').length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-blue-600 mb-4 flex items-center gap-2">
                      <Home className="w-5 h-5" /> Looking to Rent ({propertySeekers.filter(s => s.looking_for === 'rent').length})
                    </h3>
                    <div className="grid gap-4">
                      {propertySeekers
                        .filter(s => s.looking_for === 'rent')
                        .sort((a, b) => (a.status === 'new' ? -1 : 1))
                        .map((seeker) => (
                        <Card key={seeker.id} className={`shadow-lg border-l-4 ${
                          seeker.status === 'new' ? 'border-l-green-500' :
                          seeker.status === 'contacted' ? 'border-l-blue-500' :
                          seeker.status === 'closed' ? 'border-l-gray-400' : 'border-l-amber-500'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col lg:flex-row justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <h4 className="font-bold text-lg">{seeker.name}</h4>
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(seeker.status)}`}>
                                    {seeker.status.replace('_', ' ')}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                    🏠 Rent
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <Bed className="w-3 h-3 inline mr-1" />{seeker.min_bedrooms}-{seeker.max_bedrooms} beds
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-500 text-xs">Contact</p>
                                    <p className="font-medium">{seeker.email}</p>
                                    <p>{seeker.phone}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Property Type</p>
                                    <p className="font-medium capitalize">{seeker.property_type}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Budget (Monthly)</p>
                                    <p className="font-bold text-[#FF8C42]">
                                      £{seeker.min_budget?.toLocaleString()} - £{seeker.max_budget?.toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Move-in Date</p>
                                    <p className="font-medium">{seeker.move_in_date || 'Flexible'}</p>
                                  </div>
                                </div>
                                {seeker.preferred_areas?.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-gray-500 text-xs">Areas: </span>
                                    {seeker.preferred_areas.map(area => (
                                      <span key={area} className="text-xs bg-gray-100 px-2 py-1 rounded mr-1">{area}</span>
                                    ))}
                                  </div>
                                )}
                                {seeker.additional_requirements && (
                                  <p className="text-sm text-gray-600 mt-2 italic">"{seeker.additional_requirements}"</p>
                                )}
                                {seeker.assigned_to && (
                                  <p className="text-sm text-green-600 mt-2 font-medium">
                                    ✓ Assigned to: {employees.find(e => e.id === seeker.assigned_to)?.name || 'Employee'}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 min-w-[180px]">
                                {!seeker.assigned_to && seeker.status === 'new' && (
                                  <select 
                                    className="p-2 border rounded-lg text-sm bg-amber-50 border-amber-300"
                                    onChange={(e) => e.target.value && handleAssignSeeker(seeker.id, e.target.value)}
                                    defaultValue=""
                                  >
                                    <option value="">⚡ Assign to Employee</option>
                                    {filteredEmployees.filter(e => e.invitation_accepted).map(emp => (
                                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                  </select>
                                )}
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => startNewConversation(seeker.id, seeker.name, 'client')} title="Chat">
                                    <MessageSquare className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="outline" asChild title="Call">
                                    <a href={`tel:${seeker.phone}`}><Phone className="w-4 h-4" /></a>
                                  </Button>
                                  <Button size="sm" variant="outline" asChild title="Email">
                                    <a href={`mailto:${seeker.email}`}><Mail className="w-4 h-4" /></a>
                                  </Button>
                                </div>
                                <select 
                                  className="p-2 border rounded-lg text-sm"
                                  value={seeker.status}
                                  onChange={(e) => handleUpdateSeekerStatus(seeker.id, e.target.value)}
                                >
                                  <option value="new">🟢 New</option>
                                  <option value="contacted">🔵 Contacted</option>
                                  <option value="viewing_scheduled">🟡 Viewing Scheduled</option>
                                  <option value="offer_made">🟠 Offer Made</option>
                                  <option value="closed">⚫ Closed</option>
                                </select>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sale Section */}
                {(filterStatus === 'all' || filterStatus === 'sale') && propertySeekers.filter(s => s.looking_for === 'sale').length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-purple-600 mb-4 flex items-center gap-2">
                      <PoundSterling className="w-5 h-5" /> Looking to Buy ({propertySeekers.filter(s => s.looking_for === 'sale').length})
                    </h3>
                    <div className="grid gap-4">
                      {propertySeekers
                        .filter(s => s.looking_for === 'sale')
                        .sort((a, b) => (a.status === 'new' ? -1 : 1))
                        .map((seeker) => (
                        <Card key={seeker.id} className={`shadow-lg border-l-4 ${
                          seeker.status === 'new' ? 'border-l-green-500' :
                          seeker.status === 'contacted' ? 'border-l-blue-500' :
                          seeker.status === 'closed' ? 'border-l-gray-400' : 'border-l-amber-500'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col lg:flex-row justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <h4 className="font-bold text-lg">{seeker.name}</h4>
                                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(seeker.status)}`}>
                                    {seeker.status.replace('_', ' ')}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                    💰 Buy
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <Bed className="w-3 h-3 inline mr-1" />{seeker.min_bedrooms}-{seeker.max_bedrooms} beds
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-500 text-xs">Contact</p>
                                    <p className="font-medium">{seeker.email}</p>
                                    <p>{seeker.phone}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Property Type</p>
                                    <p className="font-medium capitalize">{seeker.property_type}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Budget</p>
                                    <p className="font-bold text-[#FF8C42]">
                                      £{seeker.min_budget?.toLocaleString()} - £{seeker.max_budget?.toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 text-xs">Timeline</p>
                                    <p className="font-medium">{seeker.move_in_date || 'Flexible'}</p>
                                  </div>
                                </div>
                                {seeker.preferred_areas?.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-gray-500 text-xs">Areas: </span>
                                    {seeker.preferred_areas.map(area => (
                                      <span key={area} className="text-xs bg-gray-100 px-2 py-1 rounded mr-1">{area}</span>
                                    ))}
                                  </div>
                                )}
                                {seeker.additional_requirements && (
                                  <p className="text-sm text-gray-600 mt-2 italic">"{seeker.additional_requirements}"</p>
                                )}
                                {seeker.assigned_to && (
                                  <p className="text-sm text-green-600 mt-2 font-medium">
                                    ✓ Assigned to: {employees.find(e => e.id === seeker.assigned_to)?.name || 'Employee'}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 min-w-[180px]">
                                {!seeker.assigned_to && seeker.status === 'new' && (
                                  <select 
                                    className="p-2 border rounded-lg text-sm bg-amber-50 border-amber-300"
                                    onChange={(e) => e.target.value && handleAssignSeeker(seeker.id, e.target.value)}
                                    defaultValue=""
                                  >
                                    <option value="">⚡ Assign to Employee</option>
                                    {filteredEmployees.filter(e => e.invitation_accepted).map(emp => (
                                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                  </select>
                                )}
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => startNewConversation(seeker.id, seeker.name, 'client')} title="Chat">
                                    <MessageSquare className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="outline" asChild title="Call">
                                    <a href={`tel:${seeker.phone}`}><Phone className="w-4 h-4" /></a>
                                  </Button>
                                  <Button size="sm" variant="outline" asChild title="Email">
                                    <a href={`mailto:${seeker.email}`}><Mail className="w-4 h-4" /></a>
                                  </Button>
                                </div>
                                <select 
                                  className="p-2 border rounded-lg text-sm"
                                  value={seeker.status}
                                  onChange={(e) => handleUpdateSeekerStatus(seeker.id, e.target.value)}
                                >
                                  <option value="new">🟢 New</option>
                                  <option value="contacted">🔵 Contacted</option>
                                  <option value="viewing_scheduled">🟡 Viewing Scheduled</option>
                                  <option value="offer_made">🟠 Offer Made</option>
                                  <option value="closed">⚫ Closed</option>
                                </select>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ============== MESSAGES TAB ============== */}
        {activeTab === 'messages' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Conversation List */}
            <Card className="shadow-lg md:col-span-1 h-[600px] overflow-hidden">
              <CardContent className="p-4 h-full flex flex-col">
                <h3 className="font-bold mb-4">Conversations</h3>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {conversations.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No conversations yet</p>
                  ) : (
                    conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => openConversation(conv.id)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${
                          activeConversation?.id === conv.id 
                            ? 'bg-[#FF8C42]/10 border border-[#FF8C42]' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {conv.participant_names?.filter(n => n !== adminName).join(', ') || 'Chat'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {conv.last_message || 'No messages'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-2">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="shadow-lg md:col-span-2 h-[600px]">
              <CardContent className="p-4 h-full flex flex-col">
                {activeConversation ? (
                  <>
                    <div className="flex justify-between items-center pb-3 border-b mb-3">
                      <div>
                        <h3 className="font-bold">
                          {activeConversation.participant_names?.filter(n => n !== adminName).join(', ')}
                        </h3>
                        <p className="text-xs text-gray-500">{activeConversation.subject || 'Conversation'}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setActiveConversation(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] p-3 rounded-lg ${
                            msg.sender_type === 'admin' 
                              ? 'bg-[#FF8C42] text-white' 
                              : 'bg-gray-100'
                          }`}>
                            <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                        className="flex-1"
                      />
                      <Button 
                        onClick={sendChatMessage} 
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-[#FF8C42] hover:bg-[#FF6B35]"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a conversation or start a new chat</p>
                      <p className="text-sm mt-2">Click the message icon on any lead or seeker to chat</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* ============== MODALS ============== */}
      
      {/* Invite Employee Modal */}
      {showModal === 'invite' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Invite Employee</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleInviteEmployee} className="space-y-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    placeholder="employee@email.com"
                    required
                  />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Role *</Label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  >
                    <option value="lettings_agent">Lettings Agent</option>
                    <option value="sales_agent">Sales Agent</option>
                    <option value="architectural_designer">Architectural Designer</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Send className="w-4 h-4 mr-2" /> Send Invitation
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowModal(null)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Task Modal */}
      {showModal === 'task' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Create Task</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                    placeholder="Task title"
                    required
                  />
                </div>
                <div>
                  <Label>Assign To *</Label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={taskForm.assigned_to}
                    onChange={(e) => setTaskForm({...taskForm, assigned_to: e.target.value})}
                    required
                  >
                    <option value="">Select Employee</option>
                    {filteredEmployees.filter(e => e.invitation_accepted).map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Priority</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={taskForm.priority}
                      onChange={(e) => setTaskForm({...taskForm, priority: e.target.value})}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                    rows={3}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    Create Task
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowModal(null)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Blog Post Modal */}
      {showModal === 'blog' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-4xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
                <Button variant="ghost" size="sm" onClick={() => { setShowModal(null); setEditingBlog(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Title *</Label>
                    <Input 
                      value={blogForm.title} 
                      onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                      placeholder="Enter blog post title"
                    />
                  </div>
                  <div>
                    <Label>URL Slug</Label>
                    <Input 
                      value={blogForm.slug} 
                      onChange={(e) => setBlogForm({...blogForm, slug: e.target.value})}
                      placeholder="auto-generated-from-title"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from title</p>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select 
                      className="w-full p-2 border rounded"
                      value={blogForm.category}
                      onChange={(e) => setBlogForm({...blogForm, category: e.target.value})}
                    >
                      <option value="landlord">Landlord Tips</option>
                      <option value="architecture">Architecture</option>
                      <option value="news">News & Updates</option>
                      <option value="guides">Guides</option>
                      <option value="area">Local Area</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Excerpt (Short Description) *</Label>
                  <Textarea 
                    value={blogForm.excerpt} 
                    onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                    placeholder="Brief summary shown in blog listing..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Featured Image URL</Label>
                  <Input 
                    value={blogForm.image} 
                    onChange={(e) => setBlogForm({...blogForm, image: e.target.value})}
                    placeholder="https://images.unsplash.com/..."
                  />
                  {blogForm.image && (
                    <img src={blogForm.image} alt="Preview" className="mt-2 h-32 object-cover rounded" />
                  )}
                </div>

                <div>
                  <Label>Content (Markdown supported) *</Label>
                  <Textarea 
                    value={blogForm.content} 
                    onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                    placeholder="## Your heading&#10;&#10;Write your blog content here. Markdown formatting is supported.&#10;&#10;- Bullet points&#10;- Like this&#10;&#10;**Bold text** and *italic text* work too."
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tags (comma separated)</Label>
                    <Input 
                      value={blogForm.tags} 
                      onChange={(e) => setBlogForm({...blogForm, tags: e.target.value})}
                      placeholder="Property, Investment, Harrow"
                    />
                  </div>
                  <div>
                    <Label>Read Time</Label>
                    <Input 
                      value={blogForm.read_time || '5 min read'} 
                      onChange={(e) => setBlogForm({...blogForm, read_time: e.target.value})}
                      placeholder="5 min read"
                    />
                  </div>
                </div>

                {/* SEO Fields */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Search className="w-4 h-4" /> SEO Settings
                  </h4>
                  <div className="grid gap-3">
                    <div>
                      <Label>Meta Title (SEO)</Label>
                      <Input 
                        value={blogForm.meta_title || ''} 
                        onChange={(e) => setBlogForm({...blogForm, meta_title: e.target.value})}
                        placeholder="Leave empty to use post title"
                      />
                    </div>
                    <div>
                      <Label>Meta Description (SEO)</Label>
                      <Textarea 
                        value={blogForm.meta_description || ''} 
                        onChange={(e) => setBlogForm({...blogForm, meta_description: e.target.value})}
                        placeholder="Leave empty to use excerpt"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Options */}
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={blogForm.featured} 
                      onChange={(e) => setBlogForm({...blogForm, featured: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Featured Post</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={blogForm.published} 
                      onChange={(e) => setBlogForm({...blogForm, published: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Publish Immediately</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]" onClick={handleSaveBlog}>
                    <Save className="w-4 h-4 mr-2" /> {editingBlog ? 'Update Post' : 'Save Post'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowModal(null); setEditingBlog(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Tenant Modal */}
      {showModal === 'tenant' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Add New Tenant</h3>
                <Button variant="ghost" size="sm" onClick={() => { setShowModal(null); resetTenantForm(); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleCreateTenant} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {/* Tenant Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#FF8C42] mb-3">Tenant Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tenant Name *</Label>
                      <Input value={tenantForm.name} onChange={(e) => setTenantForm({...tenantForm, name: e.target.value})} required />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input value={tenantForm.phone} onChange={(e) => setTenantForm({...tenantForm, phone: e.target.value})} required />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Email *</Label>
                    <Input type="email" value={tenantForm.email} onChange={(e) => setTenantForm({...tenantForm, email: e.target.value})} required />
                  </div>
                  <div className="mt-3">
                    <Label>Property Address *</Label>
                    <Input value={tenantForm.property_address} onChange={(e) => setTenantForm({...tenantForm, property_address: e.target.value})} required />
                  </div>
                </div>

                {/* Financial Details */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-700 mb-3">Rent & Commission</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Monthly Rent (£) *</Label>
                      <Input type="number" value={tenantForm.monthly_rent} onChange={(e) => setTenantForm({...tenantForm, monthly_rent: e.target.value})} required placeholder="What tenant pays you" />
                    </div>
                    <div>
                      <Label>Commission Type *</Label>
                      <select 
                        value={tenantForm.commission_type} 
                        onChange={(e) => setTenantForm({...tenantForm, commission_type: e.target.value})} 
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="12percent">12% Standard</option>
                        <option value="custom">Custom (External Deal)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Show calculation preview */}
                  {tenantForm.monthly_rent && (
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      {tenantForm.commission_type === '12percent' ? (
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-sm text-gray-500">Rent Received</div>
                            <div className="font-bold text-lg">£{parseFloat(tenantForm.monthly_rent).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">To Landlord (88%)</div>
                            <div className="font-bold text-lg text-blue-600">£{(parseFloat(tenantForm.monthly_rent) * 0.88).toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Your Profit (12%)</div>
                            <div className="font-bold text-lg text-green-600">£{(parseFloat(tenantForm.monthly_rent) * 0.12).toFixed(2)}</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <Label>Amount to Landlord (£) *</Label>
                            <Input 
                              type="number" 
                              value={tenantForm.landlord_payment} 
                              onChange={(e) => setTenantForm({...tenantForm, landlord_payment: e.target.value})} 
                              placeholder="Fixed amount landlord receives"
                            />
                          </div>
                          {tenantForm.landlord_payment && (
                            <div className="grid grid-cols-3 gap-4 text-center p-2 bg-gray-50 rounded">
                              <div>
                                <div className="text-sm text-gray-500">Rent Received</div>
                                <div className="font-bold">£{parseFloat(tenantForm.monthly_rent).toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">To Landlord</div>
                                <div className="font-bold text-blue-600">£{parseFloat(tenantForm.landlord_payment).toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Your Profit</div>
                                <div className="font-bold text-green-600">£{(parseFloat(tenantForm.monthly_rent) - parseFloat(tenantForm.landlord_payment)).toFixed(2)}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label>Deposit (£)</Label>
                      <Input type="number" value={tenantForm.deposit_amount} onChange={(e) => setTenantForm({...tenantForm, deposit_amount: e.target.value})} />
                    </div>
                    <div>
                      <Label>Deposit Scheme</Label>
                      <select value={tenantForm.deposit_scheme} onChange={(e) => setTenantForm({...tenantForm, deposit_scheme: e.target.value})} className="w-full p-2 border rounded-md">
                        <option value="">Select...</option>
                        <option value="DPS">DPS</option>
                        <option value="TDS">TDS</option>
                        <option value="MyDeposits">MyDeposits</option>
                      </select>
                    </div>
                    <div>
                      <Label>Deposit Reference</Label>
                      <Input value={tenantForm.deposit_reference} onChange={(e) => setTenantForm({...tenantForm, deposit_reference: e.target.value})} placeholder="Ref number" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Rent Due Day</Label>
                    <Input type="number" min="1" max="28" value={tenantForm.rent_due_day} onChange={(e) => setTenantForm({...tenantForm, rent_due_day: e.target.value})} className="w-24" />
                  </div>
                </div>

                {/* Landlord Details */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-700 mb-3">Landlord Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Landlord Name</Label>
                      <Input value={tenantForm.landlord_name} onChange={(e) => setTenantForm({...tenantForm, landlord_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Landlord Phone</Label>
                      <Input value={tenantForm.landlord_phone} onChange={(e) => setTenantForm({...tenantForm, landlord_phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Email</Label>
                    <Input type="email" value={tenantForm.landlord_email} onChange={(e) => setTenantForm({...tenantForm, landlord_email: e.target.value})} />
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Address</Label>
                    <Input value={tenantForm.landlord_address} onChange={(e) => setTenantForm({...tenantForm, landlord_address: e.target.value})} />
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Bank Details (for rent forwarding)</Label>
                    <Textarea value={tenantForm.landlord_bank_details} onChange={(e) => setTenantForm({...tenantForm, landlord_bank_details: e.target.value})} placeholder="Sort code, account number, bank name..." rows={2} />
                  </div>
                </div>

                {/* Tenancy Dates */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-amber-700 mb-3">Tenancy Dates</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date *</Label>
                      <Input type="date" value={tenantForm.tenancy_start} onChange={(e) => setTenantForm({...tenantForm, tenancy_start: e.target.value})} required />
                    </div>
                    <div>
                      <Label>End Date *</Label>
                      <Input type="date" value={tenantForm.tenancy_end} onChange={(e) => setTenantForm({...tenantForm, tenancy_end: e.target.value})} required />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea value={tenantForm.notes} onChange={(e) => setTenantForm({...tenantForm, notes: e.target.value})} placeholder="Any additional notes..." rows={2} />
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">Add Tenant</Button>
                  <Button type="button" variant="outline" onClick={() => { setShowModal(null); resetTenantForm(); }}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tenant Detail/Edit Modal */}
      {showModal === 'tenant-detail' && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Tenant Details: {selectedTenant.name}</h3>
                <Button variant="ghost" size="sm" onClick={() => { setShowModal(null); setSelectedTenant(null); resetTenantForm(); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-100 rounded-lg">
                <Button size="sm" variant="outline" onClick={() => handleSendRentReminder(selectedTenant.id, true, false)}>
                  <Mail className="w-4 h-4 mr-1" /> Email Reminder
                </Button>
                <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleSendRentReminder(selectedTenant.id, false, true)}>
                  <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleCreateRentChaseTask(selectedTenant)}>
                  <UserPlus className="w-4 h-4 mr-1" /> Assign Task
                </Button>
                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => handleRecordPayment(selectedTenant.id, selectedTenant.monthly_rent)}>
                  <CheckCheck className="w-4 h-4 mr-1" /> Mark as Paid
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDeleteTenant(selectedTenant.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>

              <form onSubmit={handleUpdateTenant} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {/* Tenant Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#FF8C42] mb-3">Tenant Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tenant Name *</Label>
                      <Input value={tenantForm.name} onChange={(e) => setTenantForm({...tenantForm, name: e.target.value})} required />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input value={tenantForm.phone} onChange={(e) => setTenantForm({...tenantForm, phone: e.target.value})} required />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Email *</Label>
                    <Input type="email" value={tenantForm.email} onChange={(e) => setTenantForm({...tenantForm, email: e.target.value})} required />
                  </div>
                  <div className="mt-3">
                    <Label>Property Address *</Label>
                    <Input value={tenantForm.property_address} onChange={(e) => setTenantForm({...tenantForm, property_address: e.target.value})} required />
                  </div>
                </div>

                {/* Payment Status */}
                <div className={`p-4 rounded-lg ${
                  selectedTenant.payment_status === 'paid' ? 'bg-green-100' :
                  selectedTenant.payment_status === 'overdue' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-semibold">Payment Status: </span>
                      <span className={`px-2 py-1 rounded ${getStatusBadge(selectedTenant.payment_status)}`}>
                        {selectedTenant.payment_status?.toUpperCase()}
                      </span>
                    </div>
                    {selectedTenant.last_payment_date && (
                      <span className="text-sm">Last paid: {selectedTenant.last_payment_date}</span>
                    )}
                  </div>
                </div>

                {/* Financial Details */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-700 mb-3">Rent & Commission</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Monthly Rent (£) *</Label>
                      <Input type="number" value={tenantForm.monthly_rent} onChange={(e) => setTenantForm({...tenantForm, monthly_rent: e.target.value})} required placeholder="What tenant pays you" />
                    </div>
                    <div>
                      <Label>Commission Type *</Label>
                      <select 
                        value={tenantForm.commission_type} 
                        onChange={(e) => setTenantForm({...tenantForm, commission_type: e.target.value})} 
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="12percent">12% Standard</option>
                        <option value="custom">Custom (External Deal)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Show calculation preview */}
                  {tenantForm.monthly_rent && (
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      {tenantForm.commission_type === '12percent' ? (
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-sm text-gray-500">Rent Received</div>
                            <div className="font-bold text-lg">£{parseFloat(tenantForm.monthly_rent).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">To Landlord (88%)</div>
                            <div className="font-bold text-lg text-blue-600">£{(parseFloat(tenantForm.monthly_rent) * 0.88).toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-500">Your Profit (12%)</div>
                            <div className="font-bold text-lg text-green-600">£{(parseFloat(tenantForm.monthly_rent) * 0.12).toFixed(2)}</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <Label>Amount to Landlord (£) *</Label>
                            <Input 
                              type="number" 
                              value={tenantForm.landlord_payment} 
                              onChange={(e) => setTenantForm({...tenantForm, landlord_payment: e.target.value})} 
                              placeholder="Fixed amount landlord receives"
                            />
                          </div>
                          {tenantForm.landlord_payment && (
                            <div className="grid grid-cols-3 gap-4 text-center p-2 bg-gray-50 rounded">
                              <div>
                                <div className="text-sm text-gray-500">Rent Received</div>
                                <div className="font-bold">£{parseFloat(tenantForm.monthly_rent).toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">To Landlord</div>
                                <div className="font-bold text-blue-600">£{parseFloat(tenantForm.landlord_payment).toLocaleString()}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Your Profit</div>
                                <div className="font-bold text-green-600">£{(parseFloat(tenantForm.monthly_rent) - parseFloat(tenantForm.landlord_payment)).toFixed(2)}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label>Deposit (£)</Label>
                      <Input type="number" value={tenantForm.deposit_amount} onChange={(e) => setTenantForm({...tenantForm, deposit_amount: e.target.value})} />
                    </div>
                    <div>
                      <Label>Deposit Scheme</Label>
                      <select value={tenantForm.deposit_scheme} onChange={(e) => setTenantForm({...tenantForm, deposit_scheme: e.target.value})} className="w-full p-2 border rounded-md">
                        <option value="">Select...</option>
                        <option value="DPS">DPS</option>
                        <option value="TDS">TDS</option>
                        <option value="MyDeposits">MyDeposits</option>
                      </select>
                    </div>
                    <div>
                      <Label>Deposit Reference</Label>
                      <Input value={tenantForm.deposit_reference} onChange={(e) => setTenantForm({...tenantForm, deposit_reference: e.target.value})} placeholder="Ref number" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Rent Due Day</Label>
                    <Input type="number" min="1" max="28" value={tenantForm.rent_due_day} onChange={(e) => setTenantForm({...tenantForm, rent_due_day: e.target.value})} className="w-24" />
                  </div>
                </div>

                {/* Landlord Details */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-700 mb-3">Landlord Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Landlord Name</Label>
                      <Input value={tenantForm.landlord_name} onChange={(e) => setTenantForm({...tenantForm, landlord_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Landlord Phone</Label>
                      <Input value={tenantForm.landlord_phone} onChange={(e) => setTenantForm({...tenantForm, landlord_phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Email</Label>
                    <Input type="email" value={tenantForm.landlord_email} onChange={(e) => setTenantForm({...tenantForm, landlord_email: e.target.value})} />
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Address</Label>
                    <Input value={tenantForm.landlord_address} onChange={(e) => setTenantForm({...tenantForm, landlord_address: e.target.value})} />
                  </div>
                  <div className="mt-3">
                    <Label>Landlord Bank Details</Label>
                    <Textarea value={tenantForm.landlord_bank_details} onChange={(e) => setTenantForm({...tenantForm, landlord_bank_details: e.target.value})} placeholder="Sort code, account number..." rows={2} />
                  </div>
                </div>

                {/* Tenancy Dates */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-amber-700 mb-3">Tenancy Dates</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Start Date *</Label>
                      <Input type="date" value={tenantForm.tenancy_start} onChange={(e) => setTenantForm({...tenantForm, tenancy_start: e.target.value})} required />
                    </div>
                    <div>
                      <Label>End Date *</Label>
                      <Input type="date" value={tenantForm.tenancy_end} onChange={(e) => setTenantForm({...tenantForm, tenancy_end: e.target.value})} required />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea value={tenantForm.notes} onChange={(e) => setTenantForm({...tenantForm, notes: e.target.value})} placeholder="Any additional notes..." rows={2} />
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">Save Changes</Button>
                  <Button type="button" variant="outline" onClick={() => { setShowModal(null); setSelectedTenant(null); resetTenantForm(); }}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Listing Modal */}
      {showModal === 'listing' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Add Property Listing</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleCreateListing} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Listing Type</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={listingForm.listing_type}
                      onChange={(e) => setListingForm({...listingForm, listing_type: e.target.value})}
                    >
                      <option value="rent">To Rent</option>
                      <option value="sale">For Sale</option>
                    </select>
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={listingForm.property_type}
                      onChange={(e) => setListingForm({...listingForm, property_type: e.target.value})}
                    >
                      <option value="flat">Flat</option>
                      <option value="house">House</option>
                      <option value="studio">Studio</option>
                      <option value="detached">Detached</option>
                      <option value="semi-detached">Semi-Detached</option>
                      <option value="terraced">Terraced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Title *</Label>
                  <Input
                    value={listingForm.title}
                    onChange={(e) => setListingForm({...listingForm, title: e.target.value})}
                    placeholder="e.g., Beautiful 2 Bed Flat in Harrow"
                    required
                  />
                </div>

                <div>
                  <Label>Address *</Label>
                  <Input
                    value={listingForm.address}
                    onChange={(e) => setListingForm({...listingForm, address: e.target.value})}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={listingForm.city}
                      onChange={(e) => setListingForm({...listingForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={listingForm.postcode}
                      onChange={(e) => setListingForm({...listingForm, postcode: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bedrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.bedrooms}
                      onChange={(e) => setListingForm({...listingForm, bedrooms: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Bathrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.bathrooms}
                      onChange={(e) => setListingForm({...listingForm, bathrooms: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Price (£) *</Label>
                    <Input
                      type="number"
                      value={listingForm.price}
                      onChange={(e) => setListingForm({...listingForm, price: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={listingForm.description}
                    onChange={(e) => setListingForm({...listingForm, description: e.target.value})}
                    rows={3}
                  />
                </div>

                {/* Photo Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4" /> Property Photos
                  </Label>
                  
                  {listingForm.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {listingForm.images.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={img.url} alt="" className="w-full h-16 object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => setListingForm({...listingForm, images: listingForm.images.filter((_, idx) => idx !== i)})}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                    multiple
                    onChange={(e) => handleFileUpload(e, 'image')}
                    className="hidden"
                    id="listing-images"
                  />
                  <label
                    htmlFor="listing-images"
                    className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#FF8C42] transition-colors"
                  >
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-600">Click to upload from camera or gallery</span>
                      </>
                    )}
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]" disabled={uploading}>
                    Create Listing
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowModal(null)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Listing Modal */}
      {showModal === 'editListing' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Edit Property Listing</h3>
                <Button variant="ghost" size="sm" onClick={() => { setShowModal(null); setEditingItem(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Listing Type</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={listingForm.listing_type}
                      onChange={(e) => setListingForm({...listingForm, listing_type: e.target.value})}
                    >
                      <option value="rent">To Rent</option>
                      <option value="sale">For Sale</option>
                    </select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={listingForm.status}
                      onChange={(e) => setListingForm({...listingForm, status: e.target.value})}
                    >
                      <option value="active">Active (Live)</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="sold">Sold</option>
                      <option value="let">Let</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Property Type</Label>
                    <select
                      className="w-full p-2 border rounded-md"
                      value={listingForm.property_type}
                      onChange={(e) => setListingForm({...listingForm, property_type: e.target.value})}
                    >
                      <option value="flat">Flat</option>
                      <option value="house">House</option>
                      <option value="studio">Studio</option>
                      <option value="detached">Detached</option>
                      <option value="semi-detached">Semi-Detached</option>
                      <option value="terraced">Terraced</option>
                      <option value="bungalow">Bungalow</option>
                      <option value="maisonette">Maisonette</option>
                      <option value="penthouse">Penthouse</option>
                    </select>
                  </div>
                  <div>
                    <Label>Price (£) *</Label>
                    <Input
                      type="number"
                      value={listingForm.price}
                      onChange={(e) => setListingForm({...listingForm, price: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Title *</Label>
                  <Input
                    value={listingForm.title}
                    onChange={(e) => setListingForm({...listingForm, title: e.target.value})}
                    placeholder="e.g., Beautiful 2 Bed Flat in Harrow"
                    required
                  />
                </div>

                <div>
                  <Label>Address *</Label>
                  <Input
                    value={listingForm.address}
                    onChange={(e) => setListingForm({...listingForm, address: e.target.value})}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={listingForm.city}
                      onChange={(e) => setListingForm({...listingForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={listingForm.postcode}
                      onChange={(e) => setListingForm({...listingForm, postcode: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Bedrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.bedrooms}
                      onChange={(e) => setListingForm({...listingForm, bedrooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Bathrooms</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.bathrooms}
                      onChange={(e) => setListingForm({...listingForm, bathrooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Receptions</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.reception_rooms || 0}
                      onChange={(e) => setListingForm({...listingForm, reception_rooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Floor Area (sqft)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={listingForm.floor_area || ''}
                      onChange={(e) => setListingForm({...listingForm, floor_area: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Available From</Label>
                  <Input
                    type="date"
                    value={listingForm.available_from || ''}
                    onChange={(e) => setListingForm({...listingForm, available_from: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={listingForm.description}
                    onChange={(e) => setListingForm({...listingForm, description: e.target.value})}
                    rows={4}
                  />
                </div>

                {/* Current Images */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4" /> Property Photos ({listingForm.images?.length || 0})
                  </Label>
                  
                  {listingForm.images?.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {listingForm.images.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={typeof img === 'string' ? img : img.url} alt="" className="w-full h-16 object-cover rounded" />
                          <button
                            type="button"
                            onClick={() => setListingForm({
                              ...listingForm, 
                              images: listingForm.images.filter((_, idx) => idx !== i)
                            })}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      for (const file of files) {
                        setUploading(true);
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData });
                          if (res.ok) {
                            const data = await res.json();
                            setListingForm(prev => ({
                              ...prev,
                              images: [...(prev.images || []), data.url]
                            }));
                          }
                        } catch (err) {
                          console.error(err);
                        }
                        setUploading(false);
                      }
                    }}
                    className="hidden"
                    id="edit-listing-images"
                  />
                  <label
                    htmlFor="edit-listing-images"
                    className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-[#FF8C42] transition-colors"
                  >
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-600 text-sm">Add more photos</span>
                      </>
                    )}
                  </label>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    type="button" 
                    onClick={handleSaveEditedListing} 
                    className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]" 
                    disabled={uploading}
                  >
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowModal(null); setEditingItem(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
